import React, { useState, useEffect, useRef, useCallback, useMemo, lazy, Suspense } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, Polygon, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import DraggableMarker from './DraggableMarker';
import { useNotification } from '../contexts/NotificationContext';
import { getDroneMarkerSVG, getEffectiveHeading } from '../utils/icons';
// Import BottomWaypointPanel at the top
import BottomWaypointPanel from './BottomWaypointPanel';
import InputModal from './InputModal';
import ProgressIndicator from './ProgressIndicator';
import DeliveryDialog from './DeliveryDialog';
import PreflightChecklist from './PreflightChecklist';
import SurveyGridPanel from './SurveyGridPanel';
import { Sheet } from './mobile/MobileKit';
import { generateSurveyGrid, appendSurveyToMission } from '../utils/surveyGrid';
import { API_URL, apiFetch } from '../config/api';
import { logger } from '../utils/logger';
import { validateWaypoints } from '../utils/missionValidation';
import { layerColor } from '../utils/altitudeLayers';
import { DEFAULT_MAP_CENTER } from '../config/constants';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || '';

// Basemap styles for the map-type picker (Mapbox when token present, free providers otherwise)
const MAP_STYLES = MAPBOX_TOKEN ? [
  { id: 'satellite', label: 'Satellite', icon: 'satellite_alt', url: `https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v12/tiles/{z}/{x}/{y}?access_token=${MAPBOX_TOKEN}`, attribution: '&copy; <a href="https://www.mapbox.com/about/maps/">Mapbox</a>', tileSize: 512, zoomOffset: -1, maxNativeZoom: 22 },
  { id: 'streets', label: 'Streets', icon: 'map', url: `https://api.mapbox.com/styles/v1/mapbox/streets-v12/tiles/{z}/{x}/{y}?access_token=${MAPBOX_TOKEN}`, attribution: '&copy; <a href="https://www.mapbox.com/about/maps/">Mapbox</a>', tileSize: 512, zoomOffset: -1, maxNativeZoom: 22 },
  { id: 'dark', label: 'Dark', icon: 'dark_mode', url: `https://api.mapbox.com/styles/v1/mapbox/dark-v11/tiles/{z}/{x}/{y}?access_token=${MAPBOX_TOKEN}`, attribution: '&copy; <a href="https://www.mapbox.com/about/maps/">Mapbox</a>', tileSize: 512, zoomOffset: -1, maxNativeZoom: 22 },
  { id: 'terrain', label: 'Terrain', icon: 'terrain', url: `https://api.mapbox.com/styles/v1/mapbox/outdoors-v12/tiles/{z}/{x}/{y}?access_token=${MAPBOX_TOKEN}`, attribution: '&copy; <a href="https://www.mapbox.com/about/maps/">Mapbox</a>', tileSize: 512, zoomOffset: -1, maxNativeZoom: 22 },
] : [
  { id: 'satellite', label: 'Satellite', icon: 'satellite_alt', url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', attribution: '&copy; Esri', tileSize: 256, zoomOffset: 0, maxNativeZoom: 19 },
  { id: 'streets', label: 'Streets', icon: 'map', url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', attribution: '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>', tileSize: 256, zoomOffset: 0, maxNativeZoom: 19 },
  { id: 'dark', label: 'Dark', icon: 'dark_mode', url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', attribution: '&copy; <a href="https://carto.com/attributions">CARTO</a>', tileSize: 256, zoomOffset: 0, maxNativeZoom: 19 },
  { id: 'terrain', label: 'Terrain', icon: 'terrain', url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png', attribution: '&copy; <a href="https://opentopomap.org">OpenTopoMap</a>', tileSize: 256, zoomOffset: 0, maxNativeZoom: 17 },
];

// 3D corridor preview (reuses the Map page's renderer); lazy so it only loads
// when the planner is switched to 3D.
const Map3DRenderer = lazy(() => import('./Map3DRenderer'));

// Human-readable labels for the backend's mission start phases (start_phase in /mission/progress)
const START_PHASE_LABELS = {
  waiting_upload: 'Waiting for mission upload to finish...',
  preparing: 'Preparing mission...',
  arming: 'Arming motors...',
  taking_off: 'Taking off...',
  switching_auto: 'Switching to AUTO mode...',
};
const startPhaseLabel = (phase) => START_PHASE_LABELS[phase] || 'Starting mission...';

// Icons are defined inline in this component

// ---- Stable map sub-components (defined OUTSIDE MissionPlanner to prevent remounting on every render) ----

/**
 * Handles map click events for adding waypoints.
 * Must be defined outside the parent component so React keeps a stable component identity.
 */
function MapClickHandler({ selectedMission, onMapClick, waypointType, deliveryMode, surveyMode }) {
  useMapEvents({
    click: (e) => {
      if (selectedMission || deliveryMode || surveyMode) {
        onMapClick(e.latlng.lat, e.latlng.lng, waypointType);
      }
    },
  });
  return null;
}

/**
 * Initializes map reference, triggers resize invalidation, and centers on active drone.
 * Must be defined outside the parent component so React keeps a stable component identity.
 */
function MapInitializer({ mapRefObj, onSetMapRef, centerTarget }) {
  const map = useMap();

  useEffect(() => {
    mapRefObj.current = map;
    if (onSetMapRef) {
      onSetMapRef(mapRefObj);
    }
    if (centerTarget && centerTarget[0] && centerTarget[1] && (centerTarget[0] !== 0 || centerTarget[1] !== 0)) {
      map.setView(centerTarget, map.getZoom() || 16);
    }

    // Trigger immediate and delayed invalidation
    const timer1 = setTimeout(() => {
      map.invalidateSize();
    }, 100);

    const timer2 = setTimeout(() => {
      map.invalidateSize();
    }, 500);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, onSetMapRef]);

  return null;
}

// The open mission + its unsaved waypoints outlive this component: tab switches,
// the desktop<->mobile layout swap and a refresh all unmount it. Per drone, per
// browser tab (sessionStorage), so switching drones keeps each drone's draft.
const draftKey = (droneId) => `utms:missionDraft:${droneId}`;
// "Are these the same route?" — what decides whether the open editor has unsaved
// edits when the mission changes on another screen.
const routeKey = (wps) => JSON.stringify((wps || []).map((w) => [
  w.type, Number(w.command) || 0, Number(w.latitude) || 0, Number(w.longitude) || 0, Number(w.altitude) || 0,
  Number(w.param1) || 0, Number(w.param2) || 0, Number(w.param3) || 0, Number(w.param4) || 0,
]));
const sameRoute = (a, b) => routeKey(a) === routeKey(b);
const readDraft = (droneId) => {
  if (!droneId) return null;
  try { return JSON.parse(sessionStorage.getItem(draftKey(droneId))); } catch { return null; }
};

function MissionPlanner({ selectedDrone, onMissionUpdate, setMapRef, bottomPanelVisible, setBottomPanelVisible, missionPanelVisible, setMissionPanelVisible, allDrones = [], dronesTelemetry = {}, isMobileLayout = false }) {
  const { showToast, showConfirm } = useNotification();
  const [missions, setMissions] = useState([]);
  const [selectedMission, setSelectedMission] = useState(() => readDraft(selectedDrone)?.mission || null);
  const [waypoints, setWaypoints] = useState(() => readDraft(selectedDrone)?.waypoints || []);
  const [mapCenter, setMapCenter] = useState(DEFAULT_MAP_CENTER);
  const [homeLocation, setHomeLocation] = useState(null);
  const homeLocationRef = useRef(null); // Track home location across polling cycles (avoids stale closure)
  const initialCenterDone = useRef(false); // Track if we've centered map on drone
  const [missionProgress, setMissionProgress] = useState(null);
  const lastStartErrorRef = useRef(null); // Dedupe start-failure toasts across progress polls
  const startWatchRef = useRef(null); // Fast-poll interval while start sequence is in progress
  const [isArmed, setIsArmed] = useState(false);
  const [droneConnected, setDroneConnected] = useState(false);
  const [telemetry, setTelemetry] = useState({});
  // bottomPanelVisible state is now managed by parent (MissionsPage)
  const [selectedWaypointId, setSelectedWaypointId] = useState(null); // Track selected waypoint for highlighting
  const [missionUploaded, setMissionUploaded] = useState(false); // Track if current mission is uploaded
  const [, setLastUploadTime] = useState(null); // Track when mission was last uploaded
  const [missionRunning, setMissionRunning] = useState(false); // Track if mission is currently running
  const [isPaused, setIsPaused] = useState(false); // Track if mission is paused
  const [missionCompleted, setMissionCompleted] = useState(false); // Track if mission just completed
  const [rerouteTransition, setRerouteTransition] = useState(null); // Mid-flight temporary transition route

  // UI state for modals and progress
  const [showInputModal, setShowInputModal] = useState(false);
  const [progressState, setProgressState] = useState({ visible: false, message: '', type: 'default' });

  // Delivery dialog state
  const [showDeliveryDialog, setShowDeliveryDialog] = useState(false);
  const [deliveryCoords, setDeliveryCoords] = useState(null);
  const [showNewMissionMenu, setShowNewMissionMenu] = useState(false);
  // Survey / Grid auto-mission state. surveyMode turns map clicks into polygon
  // vertices; pendingSurvey defers entering survey mode until a just-created
  // mission exists (see handleMissionCreate).
  const [surveyMode, setSurveyMode] = useState(false);
  const [surveyPolygon, setSurveyPolygon] = useState([]); // [{ lat, lng }]
  const [pendingSurvey, setPendingSurvey] = useState(false);
  const [surveyParams, setSurveyParams] = useState({ overlap: 70, hfovDeg: 60, altitude: 50, angleDeg: 0 });
  const [missionListOpen, setMissionListOpen] = useState(true);
  const [showPreflight, setShowPreflight] = useState(false);
  const [conflictDetails, setConflictDetails] = useState(null);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [airspaceAdvisory, setAirspaceAdvisory] = useState(null); // airport-proximity advisory (non-blocking)

  const mapRef = useRef(null);
  const isDraggingWaypointRef = useRef(false);

  // 2D (edit) vs 3D (live corridor preview) for the planner map.
  const [view3D, setView3D] = useState(false);
  const [mapStyleId, setMapStyleId] = useState(MAPBOX_TOKEN ? 'satellite' : 'streets');
  const [showMapStyleMenu, setShowMapStyleMenu] = useState(false);
  const activeMapStyle = MAP_STYLES.find(s => s.id === mapStyleId) || MAP_STYLES[0];

  // Feed the in-progress mission to the 3D corridor preview (no save needed).
  const previewDrones = useMemo(() => {
    const d = (allDrones || []).find((x) => x.id === selectedDrone);
    return d ? [d] : (selectedDrone ? [{ id: selectedDrone, name: selectedDrone }] : []);
  }, [allDrones, selectedDrone]);

  const previewLocalRoutes = useMemo(() => {
    if (!selectedDrone) return [];
    const locs = (waypoints || []).filter(
      (wp) => Number.isFinite(Number(wp.latitude)) && Number.isFinite(Number(wp.longitude))
    );
    if (locs.length < 2) return [];
    // active:true keeps the route being edited bright/prominent in the preview.
    return [{
      id: selectedDrone,
      missionId: selectedMission?.id || selectedDrone,
      waypoints,
      active: true,
      home: homeLocation || null,
      rerouteTransition: rerouteTransition || null,
    }];
  }, [selectedDrone, waypoints, selectedMission, homeLocation, rerouteTransition]);

  // Leaflet is hidden (display:none) while in 3D; repaint it when returning to 2D.
  useEffect(() => {
    if (!view3D && mapRef.current) {
      const t = setTimeout(() => { try { mapRef.current.invalidateSize(); } catch { /* map gone */ } }, 60);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [view3D]);

  // Load missions and reset state ONLY when the actual drone ID changes
  useEffect(() => {
    // CRITICAL: Reset ALL per-drone state when switching drones
    // Without this, drone1's mission/waypoints bleed into drone2
    const draft = readDraft(selectedDrone);
    setSelectedMission(draft?.mission || null);
    setWaypoints(draft?.waypoints || []);
    setMissionUploaded(false);
    setLastUploadTime(null);
    setMissionRunning(false);
    setIsPaused(false);
    setMissionCompleted(false);
    setRerouteTransition(null);
    setMissionProgress(null);
    setIsArmed(false);
    setDroneConnected(false);
    setTelemetry({});
    setSelectedWaypointId(null);
    setConflictDetails(null);
    setShowConflictModal(false);
    initialCenterDone.current = false;

    if (selectedDrone) {
      loadMissions(selectedDrone);
    } else {
      setMissions([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDrone]);

  // A mission saved, uploaded or deleted on another screen of this account (web
  // or desktop) shows up here at once. An open mission without unsaved edits
  // takes the new version; with unsaved edits they are kept, and the next save
  // asks before replacing the other screen's version (see saveMission's 409).
  const plannerRef = useRef({ selectedMission, waypoints });
  plannerRef.current = { selectedMission, waypoints };
  useEffect(() => {
    const onChange = async (e) => {
      const ev = e.detail || {};
      if (ev.entity !== 'mission' || !selectedDrone || ev.droneId !== selectedDrone) return;
      loadMissions(selectedDrone);
      const { selectedMission: open, waypoints: edited } = plannerRef.current;
      if (!open || ev.id !== open.id) return;
      if (ev.action === 'deleted') {
        showToast('This mission was deleted on another screen.', 'warning');
        return;
      }
      if (!(ev.version > (open.version || 0))) return;
      try {
        const res = await apiFetch(`${API_URL}/drones/${selectedDrone}/missions/${open.id}`);
        const data = await res.json();
        if (!data.success || !data.mission) return;
        const latest = data.mission;
        if (sameRoute(edited, open.waypoints) || sameRoute(edited, latest.waypoints)) {
          setSelectedMission(latest);
          setWaypoints((latest.waypoints || []).map((wp, idx) => ({ ...wp, index: idx })));
          if (!sameRoute(open.waypoints, latest.waypoints)) showToast('Mission updated from another screen.', 'info');
        } else {
          showToast('This mission was changed on another screen. Saving will ask before replacing it.', 'warning');
        }
      } catch { /* the next event or the next save sorts it out */ }
    };
    window.addEventListener('utms:entity-changed', onChange);
    return () => window.removeEventListener('utms:entity-changed', onChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDrone]);

  // Deliberately not keyed on selectedDrone: on a drone switch the first render still
  // holds the old drone's mission, which must not be written under the new drone's
  // key. The reset effect's setState re-runs this with the matching pair.
  useEffect(() => {
    if (!selectedDrone) return;
    try {
      if (selectedMission) sessionStorage.setItem(draftKey(selectedDrone), JSON.stringify({ mission: selectedMission, waypoints }));
      else sessionStorage.removeItem(draftKey(selectedDrone));
    } catch { /* storage full or blocked: the draft just won't survive a remount */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMission, waypoints]);

  // Separately sync home location from database if it exists (no state reset)
  useEffect(() => {
    if (!selectedDrone) {
      setHomeLocation(null);
      homeLocationRef.current = null;
      return;
    }

    const droneFromDb = (allDrones || []).find((d) => d.id === selectedDrone);
    const dbHome = droneFromDb?.homeLocation;
    if (dbHome && dbHome.lat && dbHome.lon) {
      const home = { lat: Number(dbHome.lat), lon: Number(dbHome.lon) };
      setHomeLocation(home);
      homeLocationRef.current = home;
    }
  }, [selectedDrone, allDrones]);

  // Reset auto-center flag whenever selected drone changes
  useEffect(() => {
    initialCenterDone.current = false;
  }, [selectedDrone]);

  // Sync telemetry from WebSocket props (real-time, no HTTP polling needed)
  useEffect(() => {
    if (!selectedDrone) {
      setDroneConnected(false);
      setIsArmed(false);
      setTelemetry({});
      return;
    }
    const wsTel = dronesTelemetry[selectedDrone];
    if (!wsTel) return;

    setDroneConnected(!!wsTel.connected);
    setIsArmed(wsTel.armed === true);
    setTelemetry(wsTel);

    // Auto-center map on first valid telemetry for selected drone
    if (!initialCenterDone.current && wsTel.lat && wsTel.lon && (wsTel.lat !== 0 || wsTel.lon !== 0)) {
      setMapCenter([wsTel.lat, wsTel.lon]);
      if (mapRef.current) {
        // A hidden (0×0) Leaflet map throws NaN LatLng from flyTo's animation — jump instead.
        const size = mapRef.current.getSize ? mapRef.current.getSize() : null;
        if (size && (!size.x || !size.y)) mapRef.current.setView([wsTel.lat, wsTel.lon], 16, { animate: false });
        else mapRef.current.flyTo([wsTel.lat, wsTel.lon], 16, { duration: 1.0 });
        initialCenterDone.current = true;
      }
    }

    // Set home location from first valid telemetry
    if (!homeLocationRef.current && wsTel.lat && wsTel.lon && (wsTel.lat !== 0 || wsTel.lon !== 0)) {
      const home = { lat: wsTel.lat, lon: wsTel.lon };
      homeLocationRef.current = home;
      setHomeLocation(home);
    }
  }, [selectedDrone, dronesTelemetry]);

  // Auto-expire the temporary mid-flight reroute transition when drone arrives at the target waypoint
  useEffect(() => {
    if (!rerouteTransition) return;
    const droneTel = (selectedDrone && dronesTelemetry[selectedDrone]) || telemetry;
    const droneLat = Number(droneTel?.lat || droneTel?.latitude);
    const droneLon = Number(droneTel?.lon || droneTel?.longitude);
    if (!Number.isFinite(droneLat) || !Number.isFinite(droneLon)) return;

    const tLat = Number(rerouteTransition.target?.lat);
    const tLon = Number(rerouteTransition.target?.lon);
    if (!Number.isFinite(tLat) || !Number.isFinite(tLon)) return;

    const dLat = (droneLat - tLat) * 111320;
    const dLon = (droneLon - tLon) * 111320 * Math.cos((tLat * Math.PI) / 180);
    const distM = Math.hypot(dLat, dLon);

    if (distM < 15 || (missionProgress?.current_waypoint && rerouteTransition.target?.index !== undefined && missionProgress.current_waypoint > rerouteTransition.target.index)) {
      setRerouteTransition(null);
    }
  }, [selectedDrone, dronesTelemetry, telemetry, missionProgress?.current_waypoint, rerouteTransition]);

  // Poll mission progress only (5s) — telemetry comes from WS above
  useEffect(() => {
    if (!selectedDrone) {
      setMissionProgress(null);
      return;
    }

    // Initial load
    loadMissionProgress(selectedDrone);

    const interval = setInterval(() => {
      if (isDraggingWaypointRef.current) return;
      loadMissionProgress(selectedDrone);
    }, 5000);

    return () => {
      clearInterval(interval);
      // Stop the start-sequence fast-poll when drone changes or planner unmounts
      if (startWatchRef.current) {
        clearInterval(startWatchRef.current);
        startWatchRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDrone]);

  // Live deconfliction check while editing
  useEffect(() => {
    if (!selectedDrone || !waypoints || waypoints.length < 2) {
      setConflictDetails(null);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const waypointsToCheck = waypoints.map((wp, idx) => ({
          ...wp,
          index: idx
        }));
        const res = await apiFetch(
          `${API_URL}/drones/${selectedDrone}/missions/deconflict`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ waypoints: waypointsToCheck }),
          }
        );
        if (res.ok) {
          const data = await res.json();
          if (data.success && data.conflict) {
            setConflictDetails(data);
          } else {
            setConflictDetails(null);
          }
        }
      } catch (err) {
        // Silently ignore background check errors
      }
    }, 1000); // 1s debounce

    return () => clearTimeout(timer);
  }, [waypoints, selectedDrone]);

  // Live airport-proximity advisory while editing (non-blocking, informational)
  useEffect(() => {
    // Disabled zones checks/notifications for now as requested
    setAirspaceAdvisory(null);
    return;
  }, [waypoints, selectedDrone]);

  // Handle map resize and initialization
  useEffect(() => {
    let resizeTimer;
    const handleResize = () => {
      if (mapRef.current) {
        // Clear any existing resize timer to prevent stacking
        if (resizeTimer) clearTimeout(resizeTimer);

        resizeTimer = setTimeout(() => {
          // Check if map and container still exist before invalidating
          if (mapRef.current && mapRef.current.getContainer()) {
            try {
              mapRef.current.invalidateSize();
            } catch (e) {
              logger.warn('Map invalidateSize failed:', e);
            }
          }
        }, 100);
      }
    };

    // Trigger resize on component mount after a delay to ensure layout is ready
    const timeoutId = setTimeout(() => {
      handleResize();
    }, 500);

    // Handle window resize
    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timeoutId);
      if (resizeTimer) clearTimeout(resizeTimer);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Prefer relative altitude (AGL); only fall back to alt if relative_alt is unavailable/invalid.
  const getTelemetryAltitude = (tel) => {
    if (!tel) return null;
    const relativeAlt = parseFloat(tel.relative_alt);
    if (Number.isFinite(relativeAlt)) return relativeAlt;
    const fallbackAlt = parseFloat(tel.alt);
    return Number.isFinite(fallbackAlt) ? fallbackAlt : null;
  };



  const loadMissions = async (droneId) => {
    if (!droneId) return;
    try {
      const res = await apiFetch(`${API_URL}/drones/${droneId}/missions`);
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }
      const data = await res.json();
      if (data.success) {
        setMissions(data.missions || []);
      }
    } catch (error) {
      logger.error('Error loading missions:', error);
      setMissions([]);
    }
  };

  const loadMissionProgress = async (droneId) => {
    if (!droneId) return;
    try {
      const res = await apiFetch(`${API_URL}/drones/${droneId}/mission/progress`);
      if (!res.ok) {
        // Silently fail if no active mission (404 is expected)
        if (res.status !== 404) {
          logger.error('Error loading mission progress:', res.status, res.statusText);
        }
        setMissionProgress(null);
        return;
      }
      const data = await res.json();
      if (data.success) {
        setMissionProgress(data);
        // Sync mission running state from backend
        if (data.status === 'running' || data.status === 'taking_off') {
          setMissionRunning(true);
          setIsPaused(false);
          setMissionCompleted(false);
        } else if (data.status === 'paused') {
          setMissionRunning(true);
          setIsPaused(true);
          setMissionCompleted(false);
        } else if (data.status === 'completed') {
          setMissionRunning(false);
          setIsPaused(false);
          setMissionCompleted(true);
          setMissionUploaded(false); // Reset so re-start requires fresh upload
        } else if (data.status === 'aborted') {
          setMissionRunning(false);
          setIsPaused(false);
          setMissionCompleted(false);
          setMissionUploaded(false);
          // Surface background start failures (arm/takeoff errors happen after the
          // start request already returned "queued")
          if (data.last_error && lastStartErrorRef.current !== data.last_error) {
            lastStartErrorRef.current = data.last_error;
            showToast('Mission start failed: ' + data.last_error, 'error');
          }
        } else if (data.status === 'idle' || data.status === 'uploaded') {
          // Clear stale running/paused UI if backend reports no active execution
          setMissionRunning(false);
          setIsPaused(false);
          setMissionCompleted(false);
        } else {
          // Unknown status should not keep UI stuck in running state
          setMissionRunning(false);
          setIsPaused(false);
          setMissionCompleted(false);
        }
        return data;
      }
    } catch (error) {
      // Silently fail if no active mission
      if (error.message && !error.message.includes('404')) {
        logger.error('Error loading mission progress:', error);
      }
    }
    return null;
  };

  const createNewMission = async () => {
    if (!selectedDrone) {
      showToast('Please select a drone first', 'info');
      return;
    }

    // Show input modal instead of prompt()
    setShowInputModal(true);
  };

  // Handle mission creation after modal input
  const handleMissionCreate = async (missionName) => {
    try {
      setProgressState({ visible: true, message: 'Creating mission...', type: 'processing' });

      const res = await apiFetch(`${API_URL}/drones/${selectedDrone}/missions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: missionName, waypoints: [] }),
      });

      if (!res.ok) {
        const errorText = await res.text();
        let errorMessage = `HTTP ${res.status}: ${res.statusText}`;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const data = await res.json();
      if (data.success) {
        setSelectedMission(data.mission);
        setWaypoints([]);
        loadMissions(selectedDrone);
        window.dispatchEvent(new CustomEvent('utms:mission-saved', { detail: { droneId: selectedDrone } }));

        // If this mission was created via "Survey Area", drop straight into
        // survey drawing mode now that a mission exists to receive the grid.
        if (pendingSurvey) {
          setPendingSurvey(false);
          enterSurveyMode();
        } else if (isMobileLayout) {
          showToast('Tap the map to add waypoints', 'info');
        }
        // Phone: the planner panel covers most of the map — get it out of the way.
        if (isMobileLayout) setMissionPanelVisible(false);

        setProgressState({ visible: true, message: 'Mission created successfully!', type: 'success' });
        setTimeout(() => setProgressState({ visible: false, message: '', type: 'default' }), 1500);
      } else {
        throw new Error(data.message || 'Failed to create mission');
      }
    } catch (error) {
      logger.error('Error creating mission:', error);
      setProgressState({ visible: false, message: '', type: 'default' });
      showToast('Failed to create mission: ' + (error.message || 'Unknown error'), 'error');
    }
  };

  const deleteMission = async (missionId, missionName) => {
    if (!selectedDrone || !missionId) return;

    const confirmed = await showConfirm(`Delete mission "${missionName}"? This cannot be undone.`);
    if (!confirmed) return;

    try {
      const res = await apiFetch(`${API_URL}/drones/${selectedDrone}/missions/${missionId}`, {
        method: 'DELETE',
      });

      if (!res.ok) {
        const errorText = await res.text();
        let errorMessage = `HTTP ${res.status}: ${res.statusText}`;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const data = await res.json();
      if (data.success) {
        showToast(`Mission "${missionName}" deleted`, 'success');
        // If the deleted mission was selected, clear selection
        if (selectedMission?.id === missionId) {
          setSelectedMission(null);
          setWaypoints([]);
          setMissionUploaded(false);
          setMissionRunning(false);
          setIsPaused(false);
        }
        loadMissions(selectedDrone);
        window.dispatchEvent(new CustomEvent('utms:mission-saved', { detail: { droneId: selectedDrone } }));
      } else {
        throw new Error(data.message || 'Failed to delete mission');
      }
    } catch (error) {
      logger.error('Error deleting mission:', error);
      showToast('Failed to delete mission: ' + (error.message || 'Unknown error'), 'error');
    }
  };

  const selectMission = async (missionId) => {
    if (!selectedDrone) {
      showToast('Please select a drone first', 'info');
      return;
    }

    if (!missionId) {
      showToast('Invalid mission ID', 'error');
      return;
    }

    try {
      const res = await apiFetch(`${API_URL}/drones/${selectedDrone}/missions/${missionId}`);

      if (!res.ok) {
        const errorText = await res.text();
        let errorMessage = `HTTP ${res.status}: ${res.statusText}`;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const data = await res.json();
      if (data.success && data.mission) {
        setSelectedMission(data.mission);
        // Ensure waypoints have proper indices and unique IDs
        const loadedWaypoints = (data.mission.waypoints || []).map((wp, idx) => ({
          ...wp,
          index: idx,
          id: wp.id || `wp_${Date.now()}_${idx}_${Math.random().toString(36).substr(2, 5)}`
        }));
        setWaypoints(loadedWaypoints);

        // Fly map to fit mission waypoints
        if (loadedWaypoints.length > 0 && mapRef.current) {
          const validWps = loadedWaypoints.filter(wp => wp.latitude && wp.longitude && wp.latitude !== 0 && wp.longitude !== 0);
          if (validWps.length > 0) {
            const bounds = L.latLngBounds(validWps.map(wp => [wp.latitude, wp.longitude]));
            mapRef.current.flyToBounds(bounds.pad(0.3), { duration: 1.2, maxZoom: 19 });
          }
        }
      } else {
        throw new Error(data.message || 'Failed to load mission');
      }
    } catch (error) {
      logger.error('Error loading mission:', error);
      showToast('Failed to load mission: ' + (error.message || 'Unknown error'), 'error');
      // Reset state on error
      setSelectedMission(null);
      setWaypoints([]);
    }
  };



  // Resolves true once saved. `silent` skips the success toast for callers that
  // announce the save themselves (the layer picker).
  const saveMission = async (forceWaypoints = null, { silent = false } = {}) => {
    if (!selectedMission || !selectedDrone) {
      showToast('Please select a mission and drone', 'info');
      return;
    }

    const waypointsToValidate = forceWaypoints || waypoints;

    // Validate waypoints before saving
    const validation = validateWaypoints(waypointsToValidate);
    if (!validation.valid) {
      showToast('Validation error: ' + validation.message, 'error');
      return;
    }

    try {
      // Ensure waypoints have proper indices
      const waypointsToSave = waypointsToValidate.map((wp, idx) => ({
        ...wp,
        index: idx
      }));

      // Check deconfliction first (unless forced/already resolved)
      if (!forceWaypoints) {
        try {
          const deconflictRes = await apiFetch(
            `${API_URL}/drones/${selectedDrone}/missions/deconflict`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ waypoints: waypointsToSave }),
            }
          );
          if (deconflictRes.ok) {
            const deconflictData = await deconflictRes.json();
            if (deconflictData.success && deconflictData.conflict) {
              setConflictDetails(deconflictData);
              setShowConflictModal(true);
              return; // Stop saving, show avoidance modal
            }
          }
        } catch (decErr) {
          logger.warn('Deconfliction check failed:', decErr);
        }
      }

      // `version` is the one this editor loaded: if another screen saved since,
      // the server answers 409 instead of silently replacing that save.
      const putMission = (version) => apiFetch(
        `${API_URL}/drones/${selectedDrone}/missions/${selectedMission.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...selectedMission,
            version,
            waypoints: waypointsToSave,
          }),
        }
      );
      let res = await putMission(selectedMission.version);

      if (res.status === 409) {
        const conflict = await res.json().catch(() => ({}));
        if (!conflict.mission) throw new Error(conflict.message || 'This mission was changed on another screen');
        const overwrite = await showConfirm(`"${conflict.mission.name}" was changed on another screen since you opened it. Replace it with your version?`);
        if (!overwrite) {
          setSelectedMission(conflict.mission);
          setWaypoints((conflict.mission.waypoints || []).map((wp, idx) => ({ ...wp, index: idx })));
          showToast('Loaded the latest version of this mission.', 'info');
          return false;
        }
        res = await putMission(conflict.mission.version);
      }

      // Check response status before parsing
      if (!res.ok) {
        const errorText = await res.text();
        let errorMessage = `HTTP ${res.status}: ${res.statusText}`;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const data = await res.json();
      if (data.success) {
        setSelectedMission(data.mission);
        // Update waypoints state to match saved mission with proper indices
        const savedWaypoints = (data.mission.waypoints || []).map((wp, idx) => ({
          ...wp,
          index: idx
        }));
        setWaypoints(savedWaypoints);
        // Reload missions list
        await loadMissions(selectedDrone);
        // Notify the 3D map so the route appears/updates immediately.
        window.dispatchEvent(new CustomEvent('utms:mission-saved', { detail: { droneId: selectedDrone } }));
        if (!silent) showToast('Mission saved successfully', 'success');
        // A route replaced from here (a resolution, a picked layer) is not what
        // the drone holds: the next Start must upload it, or the drone flies the
        // old route while the airspace check passed the new one.
        if (forceWaypoints) setMissionUploaded(false);
        setShowConflictModal(false);
        setConflictDetails(null);
        return true;
      } else {
        throw new Error(data.message || 'Failed to save mission');
      }
    } catch (error) {
      logger.error('Error saving mission:', error);
      showToast('Failed to save mission: ' + (error.message || 'Unknown error'), 'error');
    }
  };

  const addWaypoint = (lat, lon, type = 'WAYPOINT') => {
    if (!selectedMission) {
      showToast('Please create or select a mission first', 'info');
      return;
    }

    // Validate coordinates
    if (lat < -90 || lat > 90) {
      showToast('Invalid latitude. Must be between -90 and 90 degrees.', 'warning');
      return;
    }

    if (lon < -180 || lon > 180) {
      showToast('Invalid longitude. Must be between -180 and 180 degrees.', 'warning');
      return;
    }

    // Check waypoint limit (200 waypoints max)
    if (waypoints && waypoints.length >= 200) {
      showToast('Maximum waypoint limit reached (200 waypoints)', 'warning');
      return;
    }

    // Use functional update to ensure we have the latest waypoints state
    setWaypoints(prevWaypoints => {
      const currentLength = prevWaypoints ? prevWaypoints.length : 0;

      // Set sensible defaults based on waypoint type (ArduPilot Mission Planner convention)
      let defaultAlt = 50; // Default cruise altitude for WAYPOINT
      let defaultFrame = 3; // MAV_FRAME_GLOBAL_RELATIVE_ALT
      if (type === 'TAKEOFF') {
        defaultAlt = 50; // Takeoff to 50m AGL by default
      } else if (type === 'LAND') {
        defaultAlt = 0; // Land at ground level (0m AGL)
      } else if (type === 'RTL') {
        defaultAlt = 0; // RTL uses vehicle's RTL_ALT param, altitude ignored
      }

      const newWaypoint = {
        id: `wp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        type,
        latitude: lat,
        longitude: lon,
        altitude: defaultAlt,
        frame: defaultFrame,
        command: getCommandForType(type),
        param1: 0,
        param2: 0,
        param3: 0,
        param4: 0,
        autocontinue: 1,
        index: currentLength,
      };

      return [...prevWaypoints, newWaypoint];
    });

    // Reset upload status when waypoints are modified
    setMissionUploaded(false);
  };

  // Pre-flight safety checks
  const performPreFlightChecks = () => {
    const checks = {
      passed: true,
      errors: [],
      warnings: []
    };

    // Check 1: Drone connected
    if (!droneConnected) {
      checks.passed = false;
      checks.errors.push('Drone is not connected');
      return checks;
    }

    // Check 2: GPS fix (WARNING only for indoor/development testing)
    if (!telemetry.gps_fix || telemetry.gps_fix < 3) {
      checks.warnings.push(`GPS does not have 3D fix (type: ${telemetry.gps_fix || 0})`);
    }

    // Check 3: Satellite count (WARNING only for indoor/development testing)
    if (!telemetry.satellites || telemetry.satellites < 6) {
      checks.warnings.push(`Low satellites: ${telemetry.satellites || 0}/6 recommended`);
    }

    // Check 4: Battery level
    const batteryRemainingRaw = telemetry?.battery_remaining;
    const batteryVoltageRaw = telemetry?.battery_voltage;
    const batteryRemaining = typeof batteryRemainingRaw === 'string'
      ? parseFloat(batteryRemainingRaw)
      : batteryRemainingRaw;
    const batteryVoltage = typeof batteryVoltageRaw === 'string'
      ? parseFloat(batteryVoltageRaw)
      : batteryVoltageRaw;
    const hasBatteryReading = Number.isFinite(batteryRemaining)
      && (batteryRemaining > 0 || (Number.isFinite(batteryVoltage) && batteryVoltage > 0));

    if (!hasBatteryReading) {
      checks.warnings.push('Battery telemetry unavailable (skipping battery check)');
    } else if (batteryRemaining < 20) {
      checks.warnings.push(`Low battery: ${batteryRemaining}% (20% minimum recommended, restriction disabled)`);
    } else if (batteryRemaining < 30) {
      checks.warnings.push(`Battery is low: ${batteryRemaining}%`);
    }

    // Check 5: Home location set
    if (!homeLocation || !homeLocation.lat || !homeLocation.lon) {
      checks.warnings.push('Home location not set (will use current position)');
    }

    // Check 6: Mission uploaded
    if (!missionUploaded) {
      checks.passed = false;
      checks.errors.push('Mission not uploaded to drone');
    }

    // Check 7: Waypoint count
    if (!waypoints || waypoints.length === 0) {
      checks.passed = false;
      checks.errors.push('No waypoints in mission');
    }

    return checks;
  };

  // ... (Other functions) ...



  const updateWaypoint = useCallback((id, updates) => {
    if (!id || !selectedMission) return;
    setWaypoints(prevWaypoints => {
      if (!prevWaypoints || !Array.isArray(prevWaypoints)) return prevWaypoints;
      return prevWaypoints.map(wp => {
        if (wp.id === id) {
          const updatedWp = { ...wp, ...updates };
          // Update command and defaults if type changed
          if (updates.type && updates.type !== wp.type) {
            updatedWp.command = getCommandForType(updates.type);
            // Set proper defaults for special command types (ArduPilot convention)
            if (updates.type === 'LAND') {
              updatedWp.altitude = 0; // Land at ground level
              updatedWp.frame = 3; // GLOBAL_RELATIVE_ALT
            } else if (updates.type === 'RTL') {
              updatedWp.altitude = 0; // RTL uses vehicle RTL_ALT param
              updatedWp.latitude = 0; // RTL doesn't use coordinates
              updatedWp.longitude = 0;
              updatedWp.frame = 3;
            } else if (updates.type === 'TAKEOFF') {
              updatedWp.frame = 3; // GLOBAL_RELATIVE_ALT
              // Keep altitude as-is if already set, otherwise default 50
              if (!updatedWp.altitude || updatedWp.altitude === 0) {
                updatedWp.altitude = 50;
              }
            } else if (updates.type === 'CHANGE_SPEED') {
              // CHANGE_SPEED is a non-location command.
              updatedWp.frame = 2; // MAV_FRAME_MISSION
              updatedWp.latitude = 0;
              updatedWp.longitude = 0;
              updatedWp.altitude = 0;

              // Sensible defaults for Copter mission usage.
              if (!Number.isFinite(Number(updatedWp.param1))) {
                updatedWp.param1 = 1; // groundspeed
              }
              if (!Number.isFinite(Number(updatedWp.param2)) || Number(updatedWp.param2) <= 0) {
                updatedWp.param2 = 6; // m/s target speed
              }
              if (!Number.isFinite(Number(updatedWp.param3)) || Number(updatedWp.param3) === 0) {
                updatedWp.param3 = -1; // keep throttle unchanged
              }
            }
          }
          return updatedWp;
        }
        return wp;
      });
    });
    // Reset upload status when waypoints are modified
    setMissionUploaded(false);
  }, [selectedMission]);

  const handleWaypointDragStart = useCallback(() => {
    isDraggingWaypointRef.current = true;
  }, []);

  const handleWaypointDragEnd = useCallback((id, lat, lon) => {
    updateWaypoint(id, { latitude: lat, longitude: lon });
    isDraggingWaypointRef.current = false;
  }, [updateWaypoint]);

  const deleteWaypoint = (id) => {
    if (!id || !selectedMission) return;
    setWaypoints(prevWaypoints => {
      if (!prevWaypoints || !Array.isArray(prevWaypoints)) return prevWaypoints;
      const updated = prevWaypoints.filter(wp => wp.id !== id);
      return updated.map((wp, idx) => ({ ...wp, index: idx }));
    });
    // Reset upload status when waypoints are modified
    setMissionUploaded(false);
  };

  const reorderWaypoint = (fromIndex, toIndex) => {
    if (!selectedMission) return;

    setWaypoints(prevWaypoints => {
      if (!prevWaypoints || !Array.isArray(prevWaypoints)) return prevWaypoints;

      // Validate indices
      if (fromIndex < 0 || toIndex < 0 ||
        fromIndex >= prevWaypoints.length || toIndex >= prevWaypoints.length ||
        fromIndex === toIndex) {
        return prevWaypoints;
      }

      const newWaypoints = [...prevWaypoints];
      const [removed] = newWaypoints.splice(fromIndex, 1);
      newWaypoints.splice(toIndex, 0, removed);
      return newWaypoints.map((wp, idx) => ({ ...wp, index: idx }));
    });
    // Reset upload status when waypoints are modified
    setMissionUploaded(false);
  };

  // The backend climbs over claimed airspace on upload; show the operator the
  // route that was actually sent to the drone, and why it changed.
  const applyDeconfliction = (dc) => {
    if (!dc) return;
    if (!dc.adjusted) {
      showToast(dc.message, 'warning');
      return;
    }
    if (Array.isArray(dc.waypoints)) setWaypoints(dc.waypoints);
    showToast(`${dc.summary} Use Altitude Layers to pick another.`, 'warning');
  };

  // Altitude-layer picker: every layer for the current route, free / occupied /
  // unusable. Picking a free one saves the route moved to that layer.
  const [layerPicker, setLayerPicker] = useState(null); // null | { loading } | layer options
  const openLayerPicker = async () => {
    if (!selectedDrone || waypoints.length < 2) {
      showToast('Add at least two waypoints first', 'info');
      return;
    }
    setLayerPicker({ loading: true });
    try {
      const res = await apiFetch(`${API_URL}/drones/${selectedDrone}/missions/layers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ waypoints }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || `HTTP ${res.status}`);
      if (!data.layersEnabled) {
        setLayerPicker(null);
        showToast('Altitude layers are switched off in Settings → Airspace & Corridors', 'info');
        return;
      }
      setLayerPicker(data);
    } catch (e) {
      setLayerPicker(null);
      showToast('Could not load altitude layers: ' + e.message, 'error');
    }
  };
  const pickLayer = async (l) => {
    setLayerPicker(null);
    if (await saveMission(l.waypoints, { silent: true })) {
      showToast(`Saved on layer ${l.layer} (${l.altitude} m). Upload to send it to the drone.`, 'success');
    }
  };

  const uploadMission = async () => {
    if (!selectedMission || !selectedDrone) {
      showToast('Please select a mission and drone', 'info');
      return;
    }

    if (!waypoints || waypoints.length === 0) {
      showToast('Please add waypoints to the mission before uploading', 'info');
      return;
    }

    // Check connection status
    if (!droneConnected) {
      showToast('Drone is not connected. Please connect the drone first.', 'warning');
      return;
    }

    // Validate waypoints
    const validation = validateWaypoints(waypoints);
    if (!validation.valid) {
      showToast('Validation error: ' + validation.message, 'error');
      return;
    }

    const confirmed = await showConfirm(
      missionRunning
        ? 'Update active flight route mid-flight with new waypoints?'
        : 'Upload mission to drone? This will replace any existing mission.'
    );
    if (!confirmed) {
      return;
    }

    setProgressState({
      visible: true,
      message: missionRunning ? 'Updating flight route mid-flight...' : 'Uploading mission to drone...',
      type: 'upload'
    });

    try {
      // Quick health check — make sure backend is reachable before starting upload
      try {
        const healthRes = await apiFetch(`${API_URL}/health`, { signal: AbortSignal.timeout(5000) });
        if (!healthRes.ok) throw new Error('Backend not healthy');
      } catch {
        showToast('Backend server is not reachable. Please make sure it is running.', 'error');
        setProgressState({ visible: false, message: '', type: 'default' });
        return;
      }

      // Ensure waypoints have proper indices before upload
      const waypointsToUpload = waypoints.map((wp, idx) => ({
        ...wp,
        index: idx
      }));

      // Create abort controller for timeout (120 seconds — upload + download-verify can be slow on serial)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000);

      const res = await apiFetch(
        `${API_URL}/drones/${selectedDrone}/missions/${selectedMission.id}/upload`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ waypoints: waypointsToUpload }),
          signal: controller.signal
        }
      );

      clearTimeout(timeoutId);

      // Check response status before parsing
      if (!res.ok) {
        const errorText = await res.text();
        let errorMessage = `HTTP ${res.status}: ${res.statusText}`;
        let errorData = null;
        try {
          errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        if (errorData && errorData.needsLayerChoice) {
          // The nearest free layer is more than one away: the operator picks.
          setProgressState({ visible: false, message: '', type: 'default' });
          showToast(errorMessage, 'warning');
          openLayerPicker();
          return;
        }
        throw new Error(errorMessage);
      }

      const data = await res.json();
      if (data.success) {
        applyDeconfliction(data.deconfliction);
        setMissionUploaded(true);
        setLastUploadTime(new Date());
        if (data.reroute) {
          setRerouteTransition(data.reroute);
        } else if (missionRunning) {
          const targetWp = waypointsToUpload.find((wp, idx) => idx >= (missionProgress?.current_waypoint || 0)) || waypointsToUpload[0];
          if (targetWp && Number.isFinite(Number(telemetry?.latitude)) && Number.isFinite(Number(telemetry?.longitude))) {
            setRerouteTransition({
              droneId: selectedDrone,
              start: { lat: Number(telemetry.latitude), lon: Number(telemetry.longitude), alt: Number(telemetry.altitude) || 50 },
              target: { lat: Number(targetWp.latitude), lon: Number(targetWp.longitude), alt: Number(targetWp.altitude) || 50, index: targetWp.index },
              timestamp: Date.now(),
            });
          }
        }
        loadMissionProgress(selectedDrone).catch((e) => {
          logger.warn('Mission progress refresh failed after upload:', e);
        });
        // Telemetry handled via WS props
        showToast(missionRunning ? 'Flight route updated mid-flight!' : 'Mission uploaded successfully!', 'success');
        setProgressState({
          visible: true,
          message: missionRunning ? 'Flight route updated mid-flight!' : 'Mission uploaded successfully!',
          type: 'success'
        });
        setTimeout(() => setProgressState({ visible: false, message: '', type: 'default' }), 1500);
      } else {
        throw new Error(data.message || 'Failed to upload mission');
      }
    } catch (error) {
      logger.error('Error uploading mission:', error);

      // Handle specific error types
      let errorMessage = error.message || 'Unknown error';
      if (error.name === 'AbortError') {
        errorMessage = 'Upload timeout - took too long. Check if drone is responding and try again.';
      } else if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        errorMessage = 'Network error - cannot reach backend server. Check your connection.';
      }

      showToast('Failed to upload mission: ' + errorMessage, 'error');
      setProgressState({ visible: false, message: '', type: 'default' });
    }
  };

  const startMission = async ({ forceArm = false } = {}) => {
    if (!selectedMission || !selectedDrone) {
      showToast('Please select a mission and drone', 'info');
      return;
    }

    // Check connection status first
    if (!droneConnected) {
      showToast('Drone is not connected. Please connect the drone first.', 'warning');
      return;
    }

    // Check waypoints exist
    if (!waypoints || waypoints.length === 0) {
      showToast('No waypoints in mission. Add waypoints first.', 'error');
      return;
    }

    // Auto-upload if mission hasn't been uploaded yet (the overlay says so — no toast too)
    if (!missionUploaded) {
      setProgressState({ visible: true, message: 'Auto-uploading mission before start...', type: 'upload' });

      try {
        // Validate waypoints first
        const validation = validateWaypoints(waypoints);
        if (!validation.valid) {
          showToast('Validation error: ' + validation.message, 'error');
          setProgressState({ visible: false, message: '', type: 'default' });
          return;
        }

        const waypointsToUpload = waypoints.map((wp, idx) => ({
          ...wp,
          index: idx
        }));

        const uploadController = new AbortController();
        const uploadTimeoutId = setTimeout(() => uploadController.abort(), 120000);

        const uploadRes = await apiFetch(
          `${API_URL}/drones/${selectedDrone}/missions/${selectedMission.id}/upload`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ waypoints: waypointsToUpload }),
            signal: uploadController.signal
          }
        );

        clearTimeout(uploadTimeoutId);

        if (!uploadRes.ok) {
          const errorText = await uploadRes.text();
          let errorMessage = `Upload failed: HTTP ${uploadRes.status}`;
          let errorData = null;
          try {
            errorData = JSON.parse(errorText);
            errorMessage = errorData.message || errorMessage;
          } catch {
            errorMessage = errorText || errorMessage;
          }
          if (errorData && errorData.needsLayerChoice) {
            // The nearest free layer is more than one away: the operator picks.
            setProgressState({ visible: false, message: '', type: 'default' });
            showToast(errorMessage, 'warning');
            openLayerPicker();
            return;
          }
          throw new Error(errorMessage);
        }

        const uploadData = await uploadRes.json();
        if (!uploadData.success) {
          throw new Error(uploadData.message || 'Upload failed');
        }
        applyDeconfliction(uploadData.deconfliction);

        setMissionUploaded(true);
        setLastUploadTime(new Date());
        // Hide the spinner BEFORE the confirm dialogs below — otherwise it sits
        // on top of them and the start flow looks frozen
        setProgressState({ visible: false, message: '', type: 'default' });
      } catch (error) {
        logger.error('Auto-upload failed:', error);
        let errorMessage = error.message || 'Unknown error';
        if (error.name === 'AbortError') {
          errorMessage = 'Upload timeout - check drone connection.';
        }
        showToast('Auto-upload failed: ' + errorMessage + '. Upload manually and retry.', 'error');
        setProgressState({ visible: false, message: '', type: 'default' });
        return;
      }
    }

    // Perform pre-flight safety checks (skip mission uploaded check since we just handled it)
    const preFlightChecks = performPreFlightChecks();
    // Filter out the "Mission not uploaded" error since we just handled it
    const relevantErrors = preFlightChecks.errors.filter(e => !e.includes('Mission not uploaded'));

    const hardBlockingErrors = relevantErrors.filter(e => /Drone is not connected|Mission not uploaded to drone|No waypoints in mission/i.test(e));
    if (hardBlockingErrors.length > 0) {
      const errorMsg = 'Pre-flight check failed:\n' + hardBlockingErrors.join('\n');
      showToast(errorMsg, 'error');
      setProgressState({ visible: false, message: '', type: 'default' });
      return;
    }

    const forceArmErrors = relevantErrors.filter(e => !hardBlockingErrors.includes(e));
    let forceArmRequested = forceArm === true;

    if (forceArmErrors.length > 0 && !forceArmRequested) {
      const forceConfirm = await showConfirm(
        'Pre-flight check failed:\n' + forceArmErrors.join('\n') + '\n\nForce arm and continue anyway?'
      );
      if (!forceConfirm) {
        setProgressState({ visible: false, message: '', type: 'default' });
        return;
      }
      forceArmRequested = true;
    }

    if (forceArmErrors.length > 0 && forceArmRequested) {
      showToast('Force arm override enabled:\n' + forceArmErrors.join('\n'), 'warning');
    }

    // Show warnings if any
    if (preFlightChecks.warnings.length > 0) {
      const warningMsg = 'Warnings:\n' + preFlightChecks.warnings.join('\n');
      if (forceArmRequested) {
        showToast(warningMsg, 'warning');
      } else {
        const confirmed = await showConfirm(warningMsg + '\n\nContinue with mission start?');
        if (!confirmed) {
          setProgressState({ visible: false, message: '', type: 'default' });
          return;
        }
      }
    }

    // No longer block on isArmed or mode - backend handles GUIDED→arm→takeoff→AUTO
    // automatically; the overlay below names the step, so no toast as well.
    setProgressState({
      visible: true,
      message: forceArmRequested ? 'Starting mission (force arm override)...' : 'Starting mission (auto mode change)...',
      type: 'upload',
    });

    try {
      // Longer timeout for mission start (120s) since it includes arm+takeoff+AUTO switch
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000);

      const res = await apiFetch(
        `${API_URL}/drones/${selectedDrone}/missions/${selectedMission.id}/start`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ skip_gps_checks: true, force_arm: forceArmRequested }),
          signal: controller.signal
        }
      );

      clearTimeout(timeoutId);

      // Check response status before parsing
      if (!res.ok) {
        const errorText = await res.text();
        let errorMessage = `HTTP ${res.status}: ${res.statusText}`;
        let errorData = null;
        try {
          errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        if (errorData && errorData.conflict) {
          // An airspace conflict is resolved by choosing a free layer, not by
          // reading a technical error: open the picker with a short reason.
          setProgressState({ visible: false, message: '', type: 'default' });
          const who = errorData.details && errorData.details.droneName ? errorData.details.droneName : 'another drone';
          showToast(`Airspace conflict with ${who} — choose a free layer.`, 'warning');
          openLayerPicker();
          return;
        }
        if (errorData && errorData.needsUpload) {
          // The stored route changed since it was uploaded: upload it and retry.
          setMissionUploaded(false);
          setProgressState({ visible: false, message: '', type: 'default' });
          showToast(`${errorMessage} Press Start again to upload and start.`, 'warning');
          return;
        }
        throw new Error(errorMessage);
      }

      const data = await res.json();
      if (data.success) {
        setMissionRunning(true);
        setIsPaused(false);
        setMissionCompleted(false);
        lastStartErrorRef.current = null;
        // Do not block screen with a modal during takeoff — show toast and let the inline panel track phase
        setProgressState({ visible: false, message: '', type: 'default' });
        showToast('Mission started — vehicle arming & taking off', 'info');

        const watchStart = Date.now();
        if (startWatchRef.current) clearInterval(startWatchRef.current);
        const watch = setInterval(async () => {
          const p = await loadMissionProgress(selectedDrone);
          const timedOut = Date.now() - watchStart > 120000;
          if (p && (p.status === 'running' || p.status === 'paused' || p.status === 'completed')) {
            clearInterval(watch);
            // is_cached: the server's own state (MQTT drones cannot report
            // progress), not proof the vehicle finished its takeoff.
            if (!p.is_cached || p.status === 'completed') {
              showToast(p.status === 'completed' ? 'Mission completed!' : 'Takeoff complete — mission running!', 'success');
            }
          } else if (p && (p.status === 'aborted' || p.status === 'idle')) {
            clearInterval(watch);
          } else if (timedOut) {
            clearInterval(watch);
            showToast('Mission start is taking longer than expected — check telemetry.', 'warning');
          }
        }, 2000);
        startWatchRef.current = watch;
      } else {
        throw new Error(data.message || 'Failed to start mission');
      }
    } catch (error) {
      logger.error('Error starting mission:', error);

      let errorMessage = error.message || 'Unknown error';
      if (error.name === 'AbortError') {
        errorMessage = 'Mission start timeout - the drone may still be arming/taking off. Check telemetry.';
      } else if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        errorMessage = 'Network error - cannot reach backend server.';
      }

      showToast('Failed to start mission: ' + errorMessage, 'error');
      setProgressState({ visible: false, message: '', type: 'default' });
    }
  };

  const pauseMission = async () => {
    if (!selectedMission || !selectedDrone) {
      showToast('Please select a mission and drone', 'info');
      return;
    }

    if (!droneConnected) {
      showToast('Drone is not connected', 'warning');
      return;
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      const res = await apiFetch(
        `${API_URL}/drones/${selectedDrone}/missions/${selectedMission.id}/pause`,
        {
          method: 'POST',
          signal: controller.signal
        }
      );

      clearTimeout(timeoutId);

      // Check response status before parsing
      if (!res.ok) {
        const errorText = await res.text();
        let errorMessage = `HTTP ${res.status}: ${res.statusText}`;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const data = await res.json();
      if (data.success) {
        setIsPaused(true);
        await loadMissionProgress(selectedDrone);
        // Telemetry handled via WS props
        showToast('Mission paused successfully', 'success');
      } else {
        showToast('Failed to pause mission: ' + (data.message || 'Unknown error'), 'error');
      }
    } catch (error) {
      logger.error('Error pausing mission:', error);
      let errorMessage = error.message || 'Unknown error';
      if (error.name === 'AbortError') {
        errorMessage = 'Pause request timeout - check drone connection.';
      }
      showToast('Failed to pause mission: ' + errorMessage, 'error');
    }
  };

  const resumeMission = async () => {
    if (!selectedMission || !selectedDrone) {
      showToast('Please select a mission and drone', 'info');
      return;
    }

    if (!droneConnected) {
      showToast('Drone is not connected', 'warning');
      return;
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      const res = await apiFetch(
        `${API_URL}/drones/${selectedDrone}/missions/${selectedMission.id}/resume`,
        {
          method: 'POST',
          signal: controller.signal
        }
      );

      clearTimeout(timeoutId);

      // Check response status before parsing
      if (!res.ok) {
        const errorText = await res.text();
        let errorMessage = `HTTP ${res.status}: ${res.statusText}`;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const data = await res.json();
      if (data.success) {
        setIsPaused(false);
        await loadMissionProgress(selectedDrone);
        // Telemetry handled via WS props
        showToast('Mission resumed successfully', 'success');
      } else {
        showToast('Failed to resume mission: ' + (data.message || 'Unknown error'), 'error');
      }
    } catch (error) {
      logger.error('Error resuming mission:', error);
      let errorMessage = error.message || 'Unknown error';
      if (error.name === 'AbortError') {
        errorMessage = 'Resume request timeout - check drone connection.';
      }
      showToast('Failed to resume mission: ' + errorMessage, 'error');
    }
  };

  const abortMission = async () => {
    if (!selectedMission || !selectedDrone) {
      showToast('Please select a mission and drone', 'info');
      return;
    }

    if (!droneConnected) {
      showToast('Drone is not connected', 'warning');
      return;
    }

    const confirmed = await showConfirm('Abort mission and return to launch?');
    if (!confirmed) return;

    try {
      const res = await apiFetch(
        `${API_URL}/drones/${selectedDrone}/missions/${selectedMission.id}/abort`,
        { method: 'POST' }
      );

      // Check response status before parsing
      if (!res.ok) {
        const errorText = await res.text();
        let errorMessage = `HTTP ${res.status}: ${res.statusText}`;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const data = await res.json();
      if (data.success) {
        setMissionRunning(false);
        setIsPaused(false);
        await loadMissionProgress(selectedDrone);
        // Telemetry handled via WS props
        showToast('Mission aborted - RTL initiated', 'success');
      } else {
        showToast('Failed to abort mission: ' + (data.message || 'Unknown error'), 'error');
      }
    } catch (error) {
      logger.error('Error aborting mission:', error);
      showToast('Failed to abort mission: ' + (error.message || 'Unknown error'), 'error');
    }
  };

  const armDrone = async () => {
    if (!selectedDrone) {
      showToast('Please select a drone first', 'info');
      return;
    }

    if (!droneConnected) {
      showToast('Drone is not connected', 'warning');
      return;
    }

    const confirmed = await showConfirm('Arm the drone? Make sure the area is clear and safe.');
    if (!confirmed) {
      return;
    }

    try {
      const res = await apiFetch(`${API_URL}/arm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ droneId: selectedDrone }),
      });

      if (!res.ok) {
        const errorText = await res.text();
        let errorMessage = `HTTP ${res.status}: ${res.statusText}`;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const data = await res.json();
      if (data.success) {
        setIsArmed(true);
        // Refresh telemetry to get latest armed status
        // Telemetry handled via WS props
        showToast('Drone armed successfully', 'success');
      } else {
        throw new Error(data.message || 'Failed to arm drone');
      }
    } catch (error) {
      logger.error('Error arming drone:', error);
      showToast('Failed to arm drone: ' + (error.message || 'Unknown error'), 'error');
    }
  };

  const disarmDrone = async () => {
    if (!selectedDrone) {
      showToast('Please select a drone first', 'info');
      return;
    }

    const confirmed = await showConfirm('Disarm the drone?');
    if (!confirmed) {
      return;
    }

    try {
      const res = await apiFetch(`${API_URL}/disarm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ droneId: selectedDrone }),
      });

      if (!res.ok) {
        const errorText = await res.text();
        let errorMessage = `HTTP ${res.status}: ${res.statusText}`;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const data = await res.json();
      if (data.success) {
        setIsArmed(false);
        // Refresh telemetry to get latest armed status
        // Telemetry handled via WS props
        showToast('Drone disarmed successfully', 'success');
      } else {
        throw new Error(data.message || 'Failed to disarm drone');
      }
    } catch (error) {
      logger.error('Error disarming drone:', error);
      showToast('Failed to disarm drone: ' + (error.message || 'Unknown error'), 'error');
    }
  };

  const takeoffDrone = async () => {
    if (!selectedDrone) {
      showToast('Please select a drone first', 'info');
      return;
    }
    if (!droneConnected) {
      showToast('Drone is not connected', 'warning');
      return;
    }

    const altStr = prompt('Enter takeoff altitude in meters (1-100):', '10');
    if (altStr === null) return;
    const altitude = parseFloat(altStr);
    if (isNaN(altitude) || altitude < 1 || altitude > 100) {
      showToast('Altitude must be between 1 and 100 meters', 'error');
      return;
    }

    const confirmed = await showConfirm(`Takeoff to ${altitude}m? Make sure the area is clear.`);
    if (!confirmed) return;

    try {
      const res = await apiFetch(`${API_URL}/takeoff`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ droneId: selectedDrone, altitude }),
      });

      if (!res.ok) {
        const errorText = await res.text();
        let errorMessage = `HTTP ${res.status}: ${res.statusText}`;
        try { errorMessage = JSON.parse(errorText).message || errorMessage; } catch {}
        throw new Error(errorMessage);
      }

      const data = await res.json();
      if (data.success) {
        setIsArmed(true);
        // Telemetry handled via WS props
        await loadMissionProgress(selectedDrone);
        showToast(`Taking off to ${altitude}m`, 'success');
      } else {
        throw new Error(data.message || 'Takeoff failed');
      }
    } catch (error) {
      logger.error('Error taking off:', error);
      showToast('Takeoff failed: ' + (error.message || 'Unknown error'), 'error');
    }
  };

  const landDrone = async () => {
    if (!selectedDrone) {
      showToast('Please select a drone first', 'info');
      return;
    }
    if (!droneConnected) {
      showToast('Drone is not connected', 'warning');
      return;
    }

    const confirmed = await showConfirm('Force-land now? This overrides any running mission and lands at the current position.');
    if (!confirmed) return;

    try {
      // force:true so the backend lets LAND through even while a mission is
      // running (otherwise it returns 409 "LAND blocked while mission is running").
      const res = await apiFetch(`${API_URL}/land`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ droneId: selectedDrone, force: true }),
      });

      if (!res.ok) {
        const errorText = await res.text();
        let errorMessage = `HTTP ${res.status}: ${res.statusText}`;
        try { errorMessage = JSON.parse(errorText).message || errorMessage; } catch {}
        throw new Error(errorMessage);
      }

      const data = await res.json();
      if (data.success) {
        // Telemetry handled via WS props
        await loadMissionProgress(selectedDrone);
        showToast('Landing initiated', 'success');
      } else {
        throw new Error(data.message || 'Land failed');
      }
    } catch (error) {
      logger.error('Error landing:', error);
      showToast('Landing failed: ' + (error.message || 'Unknown error'), 'error');
    }
  };

  const forceDisarmDrone = async () => {
    if (!selectedDrone) {
      showToast('Please select a drone first', 'info');
      return;
    }
    if (!droneConnected) {
      showToast('Drone is not connected', 'warning');
      return;
    }

    const confirmed = await showConfirm(
      '⚠️ EMERGENCY FORCE DISARM ⚠️\n\n' +
      'This will IMMEDIATELY kill all motors regardless of flight state!\n' +
      'If the drone is airborne, it WILL crash.\n\n' +
      'Only use in emergencies when normal disarm fails.\n\n' +
      'Are you sure?'
    );
    if (!confirmed) return;

    try {
      const res = await apiFetch(`${API_URL}/force-disarm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ droneId: selectedDrone }),
      });

      if (!res.ok) {
        const errorText = await res.text();
        let errorMessage = `HTTP ${res.status}: ${res.statusText}`;
        try { errorMessage = JSON.parse(errorText).message || errorMessage; } catch {}
        throw new Error(errorMessage);
      }

      const data = await res.json();
      if (data.success) {
        setIsArmed(false);
        // Telemetry handled via WS props
        showToast('Force disarmed — motors killed', 'success');
      } else {
        throw new Error(data.message || 'Force disarm failed');
      }
    } catch (error) {
      logger.error('Error force disarming:', error);
      showToast('Force disarm failed: ' + (error.message || 'Unknown error'), 'error');
    }
  };

  const exportMission = async () => {
    if (!selectedMission || !selectedDrone) {
      showToast('Please select a mission and drone', 'info');
      return;
    }

    if (waypoints.length === 0) {
      showToast('Mission has no waypoints to export', 'warning');
      return;
    }

    try {
      const res = await apiFetch(
        `${API_URL}/drones/${selectedDrone}/missions/${selectedMission.id}/export`,
        { method: 'POST' }
      );

      if (!res.ok) {
        const errorText = await res.text();
        let errorMessage = `HTTP ${res.status}: ${res.statusText}`;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedMission.name || 'mission'}.waypoints`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      logger.error('Error exporting mission:', error);
      showToast('Failed to export mission: ' + (error.message || 'Unknown error'), 'error');
    }
  };

  const importMission = async (event) => {
    const file = event.target.files[0];
    if (!file) {
      return;
    }

    if (!selectedDrone) {
      showToast('Please select a drone first', 'info');
      return;
    }

    if (!file.name.endsWith('.waypoints')) {
      showToast('Invalid file type. Please select a .waypoints file', 'warning');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', file.name.replace('.waypoints', ''));

    try {
      const res = await apiFetch(
        `${API_URL}/drones/${selectedDrone}/missions/import`,
        {
          method: 'POST',
          body: formData,
        }
      );

      if (!res.ok) {
        const errorText = await res.text();
        let errorMessage = `HTTP ${res.status}: ${res.statusText}`;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const data = await res.json();
      if (data.success) {
        setSelectedMission(data.mission);
        const importedWaypoints = (data.mission.waypoints || []).map((wp, idx) => ({
          ...wp,
          id: wp.id || `wp_${Date.now()}_${idx}_${Math.random().toString(36).substr(2, 5)}`
        }));
        setWaypoints(importedWaypoints);
        loadMissions(selectedDrone);
        showToast('Mission imported successfully', 'success');
      } else {
        throw new Error(data.message || 'Failed to import mission');
      }
    } catch (error) {
      logger.error('Error importing mission:', error);
      showToast('Failed to import mission: ' + (error.message || 'Unknown error'), 'error');
    }
  };

  const getCommandForType = (type) => {
    const commands = {
      TAKEOFF: 22,
      WAYPOINT: 16,
      SURVEY: 16,
      LOITER_UNLIM: 17,
      LOITER_TURNS: 18,
      LOITER_TIME: 19,
      LAND: 21,
      RTL: 20,
      CAMERA_TRIGGER: 203,
      CHANGE_SPEED: 178,
      SET_ROI: 195,
      CONDITION_DELAY: 112,
      CONDITION_DISTANCE: 114,
      CONDITION_YAW: 115,
      CONDITION_CHANGE_ALT: 113,
    };
    return commands[type] || 16;
  };

  const prevDronePositionsRef = useRef({}); // droneId -> { lat, lon } for movement heading

  // Stable callback for setMapRef — avoids re-render churn in MapInitializer
  const stableSetMapRef = useCallback((ref) => {
    if (setMapRef) setMapRef(ref);
  }, [setMapRef]);

  // Filter out non-location waypoints from polyline path (they have no map position)
  const NON_LOCATION_WP_TYPES = [
    'RTL', 'CHANGE_SPEED', 'SET_HOME', 'DELAY', 'GUIDED_ENABLE',
    'CONDITION_DELAY', 'CONDITION_YAW', 'CONDITION_DISTANCE', 'CONDITION_CHANGE_ALT',
    'CAMERA_TRIGGER', 'IMAGE_START_CAPTURE', 'IMAGE_STOP_CAPTURE',
    'VIDEO_START_CAPTURE', 'VIDEO_STOP_CAPTURE', 'SET_CAMERA_ZOOM', 'SET_CAMERA_FOCUS',
    'DO_GIMBAL_MANAGER_PITCHYAW', 'DO_SET_SERVO', 'DO_REPEAT_SERVO',
    'DO_SET_RELAY', 'DO_REPEAT_RELAY', 'DO_GRIPPER', 'DO_AUX_FUNCTION',
    'DO_ENGINE_CONTROL', 'DO_GUIDED_LIMITS', 'DO_MOUNT_CONTROL', 'DO_JUMP',
    'SET_ROI_NONE', 'SCRIPT_TIME',
  ];
  const waypointPositions = waypoints
    .filter(wp => !NON_LOCATION_WP_TYPES.includes(wp.type) && !(wp.latitude === 0 && wp.longitude === 0))
    .map(wp => [wp.latitude, wp.longitude]);

  // ── Survey / Grid (Tier 2.4) ──────────────────────────────────────────────
  // Live grid preview: recompute whenever the area polygon or grid params change.
  // Returns null (and disables Generate) until the area has at least 3 vertices.
  const surveyPreview = useMemo(() => {
    if (!surveyMode || surveyPolygon.length < 3) return null;
    try {
      return generateSurveyGrid(surveyPolygon, surveyParams);
    } catch {
      return null;
    }
  }, [surveyMode, surveyPolygon, surveyParams]);

  const enterSurveyMode = () => {
    setView3D(false); // polygon drawing happens on the 2D map
    setSurveyPolygon([]);
    setSurveyMode(true);
  };

  const exitSurveyMode = () => {
    setSurveyMode(false);
    setSurveyPolygon([]);
  };

  const handleSurveyParamChange = (key, value) => {
    const num = parseFloat(value);
    setSurveyParams(prev => ({ ...prev, [key]: Number.isFinite(num) ? num : prev[key] }));
  };

  const undoSurveyPoint = () => setSurveyPolygon(prev => prev.slice(0, -1));
  const clearSurveyPolygon = () => setSurveyPolygon([]);

  // Commit the previewed lawnmower path as real WAYPOINTs on the current mission.
  const generateSurveyWaypoints = () => {
    if (!surveyPreview) {
      showToast('Outline an area with at least 3 points first', 'warning');
      return;
    }
    const lastType = waypoints[waypoints.length - 1]?.type;
    setWaypoints(prev => appendSurveyToMission(prev, surveyPreview.waypoints));
    setMissionUploaded(false);
    showToast(
      `Added ${surveyPreview.stats.waypointCount} survey waypoints (${surveyPreview.stats.lineCount} lines), ending with ${lastType === 'LAND' ? 'LAND' : 'RTL'}`,
      'success'
    );
    exitSurveyMode();
  };

  // Telemetry data is available through the telemetry state if needed

  // Extract fleet markers into a memoized component to avoid MapContainer re-rendering children on every telemetry ping
  const ActiveFleetMarkers = useMemo(() => {
    return allDrones.map(drone => {
      // Use high-frequency WS telemetry if available, fall back to polling data
      const currentTelemetry = dronesTelemetry[drone.id] || drone.telemetry;
      const popupAltitude = getTelemetryAltitude(currentTelemetry);
      const droneLat = currentTelemetry?.lat || 0;
      const droneLon = currentTelemetry?.lon || 0;

      // Only show drone if it has valid coordinates
      if (droneLat === 0 && droneLon === 0) return null;

      const isConnected = !!currentTelemetry?.connected;
      if (!isConnected) return null;

      const isSelected = drone.id === selectedDrone;
      const iconColor = isSelected ? '#22d3ee' : 'var(--success)';

      // Use movement-based heading (fixes arrow during RTL)
      const lastPos = prevDronePositionsRef.current[drone.id] || null;
      const droneHeading = getEffectiveHeading(currentTelemetry, lastPos);
      prevDronePositionsRef.current[drone.id] = { lat: droneLat, lon: droneLon };

      const iconSize = isSelected ? 56 : 48;

      // Create Mission Planner-style quadcopter icon
      const droneIcon = L.divIcon({
        className: `drone-marker ${isSelected ? 'drone-marker-selected' : ''}`,
        html: getDroneMarkerSVG({
          size: iconSize,
          color: iconColor,
          heading: droneHeading,
          isSelected: isSelected,
          isConnected: isConnected,
          label: drone.name?.charAt(0).toUpperCase() || 'D'
        }),
        iconSize: [iconSize, iconSize],
        iconAnchor: [iconSize / 2, iconSize / 2]
      });

      return (
        <Marker key={drone.id} position={[droneLat, droneLon]} icon={droneIcon}>
          <Popup>
            <div style={{ minWidth: '150px' }}>
              <strong style={{ color: iconColor, fontSize: '14px' }}>
                {drone.name}
              </strong>
              {isSelected && <span style={{ marginLeft: '8px', color: 'var(--accent)', fontSize: '10px' }}>● SELECTED</span>}
              <div style={{ marginTop: '8px', fontSize: '12px' }}>
                <div><strong>Status:</strong> {isConnected ? 'Connected' : 'Disconnected'}</div>
                <div><strong>Type:</strong> {drone.type || 'N/A'}</div>
                {currentTelemetry && (
                  <>
                    <div style={{ marginTop: '4px', paddingTop: '4px', borderTop: '1px solid var(--borderSecondary)' }}>
                      <strong>Position:</strong><br />{droneLat.toFixed(6)}, {droneLon.toFixed(6)}
                    </div>
                    {popupAltitude !== null && (
                      <div><strong>Altitude:</strong> {popupAltitude.toFixed(1)}m AGL</div>
                    )}
                    {currentTelemetry.battery_remaining !== undefined && (
                      <div><strong>Battery:</strong> {currentTelemetry.battery_remaining.toFixed(0)}%</div>
                    )}
                    {currentTelemetry.mode && <div><strong>Mode:</strong> {currentTelemetry.mode}</div>}
                  </>
                )}
              </div>
            </div>
          </Popup>
        </Marker>
      );
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allDrones, dronesTelemetry, selectedDrone]);

  const telemetryQuickAlt = getTelemetryAltitude(telemetry);
  const selectedDroneObj = (allDrones || []).find((d) => d.id === selectedDrone);
  const selDroneName = selectedDroneObj?.name || selectedDrone || '—';


  // New-mission choices: dropdown on desktop, bottom sheet on a phone (the panel would clip a dropdown).
  const optionStyle = {
    width: '100%',
    margin: 0,
    padding: isMobileLayout ? '12px 14px' : '8px 10px',
    background: 'transparent',
    border: 'none',
    borderRadius: '6px',
    color: 'var(--textPrimary)',
    cursor: 'pointer',
    textAlign: 'left',
    fontSize: isMobileLayout ? '14px' : '12px',
    fontWeight: 600,
    textTransform: 'none',
    letterSpacing: '0.3px',
    whiteSpace: 'nowrap',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.15s ease'
  };
  const newMissionOptions = (
    <>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setShowNewMissionMenu(false);
          createNewMission();
        }}
        style={optionStyle}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'color-mix(in srgb, var(--accent) 12%, transparent)'; e.currentTarget.style.color = 'var(--accent)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--textPrimary)'; }}
      >
        <span className="material-symbols-outlined" style={{ fontSize: '16px', color: 'var(--accent)' }}>edit</span>
        <span>Manual</span>
      </button>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setShowNewMissionMenu(false);
          setDeliveryCoords(null);
          setShowDeliveryDialog(true);
        }}
        style={optionStyle}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'color-mix(in srgb, var(--accent) 12%, transparent)'; e.currentTarget.style.color = 'var(--accent)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--textPrimary)'; }}
      >
        <span className="material-symbols-outlined" style={{ fontSize: '16px', color: 'var(--accent)' }}>bolt</span>
        <span>Quick</span>
      </button>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setShowNewMissionMenu(false);
          // Survey appends a grid to a mission. Use the selected
          // one, or create a new mission first then enter survey.
          if (selectedMission) {
            enterSurveyMode();
          } else {
            setPendingSurvey(true);
            createNewMission();
          }
        }}
        style={optionStyle}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'color-mix(in srgb, var(--accent) 12%, transparent)'; e.currentTarget.style.color = 'var(--accent)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--textPrimary)'; }}
      >
        <span className="material-symbols-outlined" style={{ fontSize: '16px', color: 'var(--accent)' }}>grid_view</span>
        <span>Survey</span>
      </button>
    </>
  );

  // Mission Controller card — desktop: floating card below Select Drones (top-right); mobile: docked in panel
  const missionControllerCard = selectedMission ? (
          <div className="custom-scrollbar" style={{
            padding: '16px',
            borderRadius: '12px',
            border: '1px solid color-mix(in srgb, var(--accent) 12%, transparent)',
            background: 'var(--bgSecondary)',
            zIndex: isMobileLayout ? 10 : 999,
            position: isMobileLayout ? 'relative' : 'absolute',
            top: isMobileLayout ? undefined : '184px',
            right: isMobileLayout ? undefined : '16px',
            width: isMobileLayout ? undefined : '300px',
            // Below the Select Drones card (top:16 + ~150px card + gap); stay clear of the
            // waypoint editor bar at the bottom (open = 288px+10px margin, collapsed = 40px)
            maxHeight: isMobileLayout ? undefined : (bottomPanelVisible ? 'calc(100% - 184px - 308px)' : 'calc(100% - 184px - 50px)'),
            overflowY: isMobileLayout ? undefined : 'auto',
            transition: isMobileLayout ? undefined : 'max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            pointerEvents: 'auto',
            flexShrink: 0,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '0 0 12px 0' }}>
              <p style={{ margin: 0, color: 'var(--textMuted)', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 600 }}>Mission Controller</p>
              <div style={{ display: 'flex', gap: '4px' }}>
                <button onClick={(e) => { e.stopPropagation(); saveMission(); }} title="Save mission changes" style={{ background: 'transparent', border: 'none', color: 'var(--textMuted)', cursor: 'pointer', display: 'flex', padding: '2px' }}>
                  <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>save</span>
                </button>
                <button onClick={(e) => { e.stopPropagation(); uploadMission(); }} title="Upload mission to drone" style={{ background: 'transparent', border: 'none', color: 'var(--accent)', cursor: 'pointer', display: 'flex', padding: '2px' }}>
                  <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>upload</span>
                </button>
                <button onClick={(e) => { e.stopPropagation(); exportMission(); }} title="Export mission file (.waypoints)" style={{ background: 'transparent', border: 'none', color: 'var(--textMuted)', cursor: 'pointer', display: 'flex', padding: '2px' }}>
                  <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>download</span>
                </button>
              </div>
            </div>
            {/* Live Collision Warning Banner */}
            {conflictDetails && (
              <div 
                onClick={() => setShowConflictModal(true)}
                style={{
                  background: 'color-mix(in srgb, var(--danger) 10%, transparent)',
                  border: '1px solid color-mix(in srgb, var(--danger) 40%, transparent)',
                  padding: '10px 12px',
                  marginBottom: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  borderRadius: '2px',
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5">
                  <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
                <div style={{ flex: 1 }}>
                  <div style={{ color: 'var(--danger)', fontWeight: 800, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Collision Warning
                  </div>
                  <div style={{ color: 'color-mix(in srgb, var(--textPrimary) 70%, transparent)', fontSize: '10px', marginTop: '2px' }}>
                    Corridor conflicts with {conflictDetails.details.droneName || 'another drone'}. Click to resolve.
                  </div>
                </div>
              </div>
            )}

            {waypoints.length >= 2 && (
              <button
                onClick={openLayerPicker}
                title="See which altitude layers are free for this route and move the cruise to one"
                style={{
                  width: '100%', marginBottom: '12px', padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '8px',
                  background: 'transparent', border: '1px solid var(--borderSecondary)', color: 'var(--textPrimary, #fff)',
                  cursor: 'pointer', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
                }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>layers</span>
                Altitude Layers
              </button>
            )}


            {/* ── Flight Control (matches Stitch image) ── */}
            <div style={{ marginTop: '12px', borderTop: '1px solid var(--borderSecondary)', paddingTop: '12px' }}>
                {/* Arm / Disarm */}
                <div style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
                  <button
                    onClick={(e) => { e.stopPropagation(); isArmed ? disarmDrone() : armDrone(); }}
                    title={isArmed ? 'Disarm drone motors' : 'Arm drone motors'}
                    style={{
                      padding: '12px',
                      background: isArmed ? 'color-mix(in srgb, var(--danger) 10%, transparent)' : 'rgba(249, 115, 22, 0.08)',
                      color: isArmed ? 'var(--danger)' : '#fb923c',
                      border: `2px solid ${isArmed ? 'var(--danger)' : 'rgba(249, 115, 22, 0.5)'}`,
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: 700,
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      textTransform: 'uppercase',
                      letterSpacing: '1px'
                    }}
                  >
                    {isArmed ? 'Disarm' : 'Arm'}
                  </button>
                </div>

                {/* Takeoff / Land */}
                <div style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
                  <button
                    onClick={(e) => { e.stopPropagation(); takeoffDrone(); }}
                    title="Takeoff to specified altitude (auto-arms if needed)"
                    style={{
                      padding: '8px 12px',
                      background: 'color-mix(in srgb, var(--success) 15%, transparent)',
                      color: 'var(--success)',
                      border: '1px solid var(--success)',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '11px',
                      fontWeight: 700,
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '4px',
                      textTransform: 'uppercase',
                    }}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>flight_takeoff</span> Takeoff
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); landDrone(); }}
                    title="Land drone at current position"
                    style={{
                      padding: '8px 12px',
                      background: 'color-mix(in srgb, var(--accent) 12%, transparent)',
                      color: 'var(--accent)',
                      border: '1px solid var(--accent)',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '11px',
                      fontWeight: 700,
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '4px',
                      textTransform: 'uppercase',
                    }}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>flight_land</span> Land
                  </button>
                </div>

                {/* Force Disarm — Emergency */}
                <button
                  onClick={(e) => { e.stopPropagation(); forceDisarmDrone(); }}
                  title="EMERGENCY: Force disarm — kills motors immediately regardless of flight state"
                  style={{
                    width: '100%',
                    padding: '6px',
                    marginBottom: '6px',
                    background: 'color-mix(in srgb, var(--danger) 10%, transparent)',
                    color: 'var(--danger)',
                    border: '1px dashed var(--danger)',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '10px',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                  }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>cancel</span> Force Disarm (Emergency)
                </button>

                {/* Pre-flight Checklist */}
                <button
                  onClick={() => setShowPreflight(true)}
                  disabled={!selectedDrone || !selectedMission}
                  style={{
                    width: '100%', padding: '6px', marginBottom: '6px',
                    background: 'color-mix(in srgb, var(--accent) 6%, transparent)', color: 'var(--accent)',
                    border: '1px solid color-mix(in srgb, var(--accent) 40%, transparent)', borderRadius: '8px', cursor: 'pointer',
                    fontSize: '11px', fontWeight: 700, textTransform: 'uppercase',
                    opacity: (!selectedDrone || !selectedMission) ? 0.5 : 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                  }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '18px', fontVariationSettings: "'FILL' 1" }}>check_box</span> Pre-flight Checklist
                </button>

                {/* Start / Pause / Resume */}
                <div style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
                  {(!missionRunning || isPaused) && (
                    <>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isPaused && missionRunning) {
                            resumeMission();
                          } else {
                            startMission();
                          }
                        }}
                        disabled={!droneConnected || (!waypoints || waypoints.length === 0)}
                        title={
                          !droneConnected ? 'Connect drone first'
                            : (!waypoints || waypoints.length === 0) ? 'Add waypoints first'
                              : isPaused ? 'Resume paused mission'
                                : !missionUploaded ? 'Start mission (auto-uploads & auto-arms)'
                                  : 'Start mission (auto-arms)'
                        }
                        style={{
                          padding: '8px 12px',
                          background: (!droneConnected || !waypoints || waypoints.length === 0)
                            ? 'rgba(107, 114, 128, 0.1)'
                            : 'color-mix(in srgb, var(--success) 15%, transparent)',
                          color: (!droneConnected || !waypoints || waypoints.length === 0) ? 'var(--textMuted)' : 'var(--success)',
                          border: `1px solid ${(!droneConnected || !waypoints || waypoints.length === 0) ? 'var(--textMuted)' : 'var(--success)'}`,
                          borderRadius: '8px',
                          cursor: (!droneConnected || !waypoints || waypoints.length === 0) ? 'not-allowed' : 'pointer',
                          fontSize: '11px',
                          fontWeight: 700,
                          flex: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '4px',
                          textTransform: 'uppercase',
                          opacity: (!droneConnected || !waypoints || waypoints.length === 0) ? 0.5 : 1
                        }}
                      >
                        {isPaused ? 'Resume' : 'Start'}
                      </button>

                    </>
                  )}
                  {missionRunning && !isPaused && (
                    <button
                      onClick={(e) => { e.stopPropagation(); pauseMission(); }}
                      title="Pause mission (drone will loiter)"
                      style={{
                        padding: '8px 12px',
                        background: 'color-mix(in srgb, var(--warning) 15%, transparent)',
                        color: 'var(--warning)',
                        border: '1px solid var(--warning)',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '11px',
                        fontWeight: 700,
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        textTransform: 'uppercase'
                      }}
                    >
                      Pause
                    </button>
                  )}
                </div>

                {/* Abort (RTL) */}
                {missionRunning && (
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button
                      onClick={(e) => { e.stopPropagation(); abortMission(); }}
                      title="Abort mission and Return To Launch"
                      style={{
                        padding: '8px 12px',
                        background: 'color-mix(in srgb, var(--danger) 20%, transparent)',
                        color: 'var(--danger)',
                        border: '1px solid var(--danger)',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '11px',
                        fontWeight: 700,
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        textTransform: 'uppercase',
                        animation: 'pulse 2s infinite'
                      }}
                    >
                      Abort (RTL)
                    </button>
                  </div>
                )}

                {/* Status indicator */}
                <div style={{
                  marginTop: '8px',
                  padding: '6px 10px',
                  background: 'var(--bgPrimary)',
                  border: '1px solid var(--borderSecondary)',
                  fontSize: '10px',
                  color: 'var(--textSecondary)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      background: missionRunning
                        ? (isPaused ? 'var(--warning)' : 'var(--success)')
                        : missionCompleted ? '#3b82f6'
                          : (isArmed ? 'var(--warning)' : '#6b7280'),
                      display: 'inline-block',
                      animation: missionRunning && !isPaused ? 'pulse 1.5s infinite' : 'none'
                    }} />
                    {missionRunning
                      ? (isPaused
                        ? 'Mission Paused'
                        : missionProgress?.status === 'taking_off'
                          ? startPhaseLabel(missionProgress?.start_phase)
                          : 'Mission Running')
                      : missionCompleted
                        ? 'Mission Completed'
                        : missionUploaded
                          ? 'Ready to Start'
                          : waypoints && waypoints.length > 0
                            ? 'Ready (will auto-upload on Start)'
                            : 'Add Waypoints to Mission'}
                  </div>
                  {/* Mission progress bar */}
                  {missionProgress && (missionRunning || missionCompleted) && (
                    <div style={{ width: '100%' }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: '9px',
                        color: 'var(--textMuted)',
                        marginBottom: '3px'
                      }}>
                        <span>WP {missionProgress.current_waypoint || 0}/{missionProgress.total_waypoints || 0}</span>
                        <span>{Math.round(missionProgress.progress || 0)}%</span>
                      </div>
                      <div style={{
                        height: '4px',
                        background: 'var(--bgSecondary)',
                        borderRadius: '2px',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          height: '100%',
                          width: `${missionProgress.progress || 0}%`,
                          background: missionCompleted ? '#3b82f6' : isPaused ? 'var(--warning)' : 'var(--success)',
                          borderRadius: '2px',
                          transition: 'width 0.5s ease'
                        }} />
                      </div>
                    </div>
                  )}
                  {/* Telemetry quick info */}
                  {droneConnected && telemetry && (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: '9px',
                      color: 'var(--textMuted)',
                      borderTop: '1px solid var(--borderSecondary)',
                      paddingTop: '4px',
                      marginTop: '2px'
                    }}>
                      <span>Mode: {telemetry.mode || 'N/A'}</span>
                      <span>Alt: {telemetryQuickAlt !== null ? `${telemetryQuickAlt.toFixed(1)}m AGL` : 'N/A'}</span>
                      <span>Bat: {telemetry.battery_remaining ? telemetry.battery_remaining.toFixed(0) + '%' : 'N/A'}</span>
                    </div>
                  )}
                </div>
              </div>

          </div>
  ) : null;

  return (
    <div style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden', position: 'relative', height: '100%' }}>
      {/* Airport-proximity advisory (non-blocking) */}
      {airspaceAdvisory && (
        <div
          style={{
            position: 'fixed',
            top: 'calc(var(--m-inset-top) + 16px)',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9998,
            width: 'calc(100% - 32px)',
            minWidth: 'min(320px, calc(100% - 32px))',
            maxWidth: '520px',
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            padding: '12px 16px',
            borderRadius: '12px',
            background: 'rgba(22, 28, 30, 0.95)',
            border: `1px solid ${airspaceAdvisory.level === 'critical' ? 'color-mix(in srgb, var(--danger) 50%, transparent)' : 'rgba(249, 115, 22, 0.5)'}`,
            color: '#fff',
            boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
            backdropFilter: 'blur(12px)',
          }}
        >
          <span className="material-symbols-outlined" style={{ color: airspaceAdvisory.level === 'critical' ? 'var(--danger)' : '#fb923c', fontSize: '22px' }}>
            warning
          </span>
          <div style={{ fontSize: '13px', lineHeight: 1.5, flex: 1 }}>
            <div style={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.02em', marginBottom: '4px', color: airspaceAdvisory.level === 'critical' ? '#f87171' : '#fb923c' }}>
              {airspaceAdvisory.level === 'critical' ? 'Airspace — No-Fly Proximity' : 'Airspace Advisory'}
            </div>
            <div style={{ opacity: 0.95 }}>{airspaceAdvisory.message}</div>
            {airspaceAdvisory.violations && airspaceAdvisory.violations.length > 1 && (
              <div style={{ opacity: 0.7, marginTop: '2px' }}>
                +{airspaceAdvisory.violations.length - 1} more aerodrome{airspaceAdvisory.violations.length - 1 > 1 ? 's' : ''} nearby
              </div>
            )}
          </div>
          <button
            onClick={() => setAirspaceAdvisory(null)}
            style={{ background: 'transparent', border: 'none', color: 'color-mix(in srgb, var(--textPrimary) 70%, transparent)', cursor: 'pointer', fontSize: '14px', lineHeight: '14px', padding: '2px' }}
            title="Dismiss"
          >
            ✕
          </button>
        </div>
      )}

      {/* Left Sidebar - Mission List & Controls.
          Floating transparent overlay so the map shows behind the cards
          (Planner + Controller float over the map, per the Stitch reference).
          Phone: same cards, stacked under the drone chip and scrolling between
          the bars; hidden (not shrunk to an icon rail) when closed. */}
      <div style={isMobileLayout ? {
        position: 'absolute',
        top: 'calc(var(--m-top) + 140px)',
        left: 16,
        width: 'min(304px, calc(100vw - 96px))',
        maxHeight: 'calc(100% - var(--m-top) - 140px - var(--m-nav) - var(--m-bottom-lift, 0px) - 16px)',
        zIndex: 1100,
        display: missionPanelVisible ? 'flex' : 'none',
        flexDirection: 'column',
        gap: '12px',
        overflowY: 'auto',
        overflowX: 'hidden',
        borderRadius: '12px',
        boxSizing: 'border-box',
      } : {
        width: missionPanelVisible ? '350px' : '92px',
        background: 'transparent',
        borderRight: 'none',
        position: 'absolute',
        top: 0,
        left: 0,
        zIndex: 1001,
        display: 'flex',
        flexDirection: 'column',
        gap: missionPanelVisible ? '12px' : '0',
        padding: '12px',
        overflowY: 'visible',
        overflowX: 'visible',
        pointerEvents: 'none',
        minHeight: 0,
        height: '100%',
        maxHeight: '100%',
        transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        flexShrink: 0,
        boxSizing: 'border-box'
      }} className="custom-scrollbar">
        {/* Header */}
        <div style={{
          padding: missionPanelVisible ? '16px' : '12px 6px',
          borderRadius: missionPanelVisible ? '12px 12px 0 0' : (missions.length > 0 ? '12px 12px 0 0' : '12px'),
          border: '1px solid color-mix(in srgb, var(--accent) 16%, transparent)',
          borderBottom: missionPanelVisible ? 'none' : (missions.length > 0 ? 'none' : '1px solid color-mix(in srgb, var(--accent) 16%, transparent)'),
          background: 'var(--bgSecondary)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.45)',
          pointerEvents: 'auto',
          flexShrink: 0,
          minHeight: missionPanelVisible ? 'auto' : '72px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: missionPanelVisible ? 'stretch' : 'center',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
          {missionPanelVisible ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '15px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{
                    width: '26px',
                    height: '26px',
                    borderRadius: '6px',
                    background: 'color-mix(in srgb, var(--accent) 12%, transparent)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <span className="material-symbols-outlined" style={{ fontSize: '16px', color: 'var(--accent)' }}>route</span>
                  </div>
                  <h2 style={{
                    margin: 0,
                    color: 'var(--accent)',
                    fontSize: '15px',
                    fontWeight: 700,
                    letterSpacing: '0.3px',
                    whiteSpace: 'nowrap'
                  }}>
                    Mission Planner
                  </h2>
                </div>
                {!isMobileLayout && (
                  <button
                    type="button"
                    onClick={() => setMissionPanelVisible(false)}
                    title="Collapse mission planner"
                    style={{
                      background: 'color-mix(in srgb, var(--textPrimary) 4%, transparent)',
                      border: '1px solid color-mix(in srgb, var(--textPrimary) 8%, transparent)',
                      color: 'var(--textMuted)',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '26px',
                      height: '26px',
                      borderRadius: '6px',
                      padding: 0,
                      transition: 'all 0.15s ease'
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.color = 'var(--accent)';
                      e.currentTarget.style.background = 'color-mix(in srgb, var(--accent) 12%, transparent)';
                      e.currentTarget.style.borderColor = 'color-mix(in srgb, var(--accent) 30%, transparent)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.color = 'var(--textMuted)';
                      e.currentTarget.style.background = 'color-mix(in srgb, var(--textPrimary) 4%, transparent)';
                      e.currentTarget.style.borderColor = 'color-mix(in srgb, var(--textPrimary) 8%, transparent)';
                    }}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>chevron_left</span>
                  </button>
                )}
                {isMobileLayout && (
                  <button type="button" className="m-b m-icon-btn" style={{ marginLeft: 'auto', marginTop: -8, marginRight: -8 }} onClick={() => setMissionPanelVisible(false)} aria-label="Close planner">
                    <span className="material-symbols-outlined" style={{ fontSize: 20 }}>close</span>
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', gap: '6px', width: '100%' }}>
                <div style={{ flex: 1, position: 'relative' }}>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowNewMissionMenu(!showNewMissionMenu);
                    }}
                    type="button"
                    style={{
                      height: '38px',
                      padding: '0 12px',
                      background: 'var(--accent)',
                      color: 'var(--bgPrimary)',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: 700,
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <span>New Mission</span>
                    <span className="material-symbols-outlined" style={{ fontSize: '18px', transition: 'transform 0.2s', transform: showNewMissionMenu ? 'rotate(180deg)' : 'rotate(0deg)' }}>expand_more</span>
                  </button>
                  {showNewMissionMenu && (isMobileLayout ? (
                    <Sheet title="NEW MISSION" onClose={() => setShowNewMissionMenu(false)}>{newMissionOptions}</Sheet>
                  ) : (
                    <>
                      {/* Invisible backdrop to close menu */}
                      <div
                        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }}
                        onClick={() => setShowNewMissionMenu(false)}
                      />
                      <div style={{
                        position: 'absolute',
                        top: 'calc(100% + 4px)',
                        left: 0,
                        right: 0,
                        background: 'rgba(20, 24, 28, 0.96)',
                        backdropFilter: 'blur(16px)',
                        WebkitBackdropFilter: 'blur(16px)',
                        border: '1px solid color-mix(in srgb, var(--accent) 25%, transparent)',
                        borderRadius: '8px',
                        zIndex: 1000,
                        boxShadow: '0 10px 28px rgba(0,0,0,0.65), 0 0 12px color-mix(in srgb, var(--accent) 12%, transparent)',
                        padding: '4px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '2px'
                      }}>
                          {newMissionOptions}
                      </div>
                    </>
                  ))}
                </div>
                <label style={{
                  height: '38px',
                  padding: '0 12px',
                  background: 'rgba(59, 130, 246, 0.1)',
                  color: 'var(--accentSecondary)',
                  border: '1px solid var(--accentSecondary)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: 600,
                  flex: 1,
                  textAlign: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '4px'
                }}>
                  <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>file_upload</span>
                  Import
                  <input
                    type="file"
                    accept=".waypoints"
                    onChange={importMission}
                    style={{ display: 'none' }}
                  />
                </label>
              </div>

              {/* Selected Drone card */}
              <div className="active-glow" style={{
                marginTop: '14px',
                background: 'rgba(27, 28, 29, 0.5)',
                border: '1px solid color-mix(in srgb, var(--accent) 22%, transparent)',
                borderRadius: '8px',
                padding: '14px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ padding: '6px', background: 'color-mix(in srgb, var(--accent) 15%, transparent)', border: '1px solid color-mix(in srgb, var(--accent) 30%, transparent)', borderRadius: '6px', display: 'flex' }}>
                    <span className="material-symbols-outlined" style={{ color: 'var(--accent)', fontSize: '20px' }}>near_me</span>
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <p style={{ margin: 0, color: 'var(--textMuted)', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Selected Drone</p>
                    <p style={{ margin: 0, color: 'var(--textPrimary)', fontSize: '16px', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {selDroneName}
                    </p>
                  </div>
                  <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: droneConnected ? 'var(--success)' : '#6b7280', display: 'inline-block' }} />
                    <span style={{ fontSize: '11px', color: 'var(--textSecondary)', whiteSpace: 'nowrap' }}>
                      {telemetry && telemetry.battery_remaining != null ? `${Math.round(telemetry.battery_remaining)}% Batt` : (droneConnected ? 'ONLINE' : 'OFFLINE')}
                    </span>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <button
              type="button"
              onClick={() => setMissionPanelVisible(true)}
              title="Open Mission Planner"
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '6px 2px',
                width: '100%',
                outline: 'none',
                gap: '6px'
              }}
              onMouseEnter={(e) => {
                const iconBox = e.currentTarget.querySelector('.collapsed-planner-btn');
                if (iconBox) {
                  iconBox.style.background = 'color-mix(in srgb, var(--accent) 20%, transparent)';
                  iconBox.style.borderColor = 'var(--accent)';
                  iconBox.style.boxShadow = '0 0 14px color-mix(in srgb, var(--accent) 45%, transparent)';
                  iconBox.style.transform = 'scale(1.06)';
                }
              }}
              onMouseLeave={(e) => {
                const iconBox = e.currentTarget.querySelector('.collapsed-planner-btn');
                if (iconBox) {
                  iconBox.style.background = 'color-mix(in srgb, var(--accent) 10%, transparent)';
                  iconBox.style.borderColor = 'color-mix(in srgb, var(--accent) 30%, transparent)';
                  iconBox.style.boxShadow = 'none';
                  iconBox.style.transform = 'scale(1)';
                }
              }}
            >
              <div
                className="collapsed-planner-btn"
                style={{
                  width: '42px',
                  height: '42px',
                  borderRadius: '10px',
                  background: 'color-mix(in srgb, var(--accent) 10%, transparent)',
                  border: '1px solid color-mix(in srgb, var(--accent) 30%, transparent)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: '24px', color: 'var(--accent)' }}>
                  route
                </span>
              </div>
              <span style={{
                fontSize: '9px',
                fontWeight: 700,
                color: 'var(--textMuted)',
                letterSpacing: '0.8px',
                textTransform: 'uppercase',
                fontFamily: "'JetBrains Mono', monospace"
              }}>
                Planner
              </span>
            </button>
          )}
        </div>

        {/* Mission List - Drone Missions card */}
        <div style={{
          flexShrink: 0,
          overflowX: 'hidden',
          padding: missionPanelVisible ? '16px' : (missions.length > 0 ? '8px 4px' : '0'),
          borderRadius: missionPanelVisible ? '0 0 12px 12px' : (missions.length > 0 ? '0 0 12px 12px' : '0'),
          border: missionPanelVisible || missions.length > 0 ? '1px solid color-mix(in srgb, var(--accent) 16%, transparent)' : 'none',
          borderTop: 'none',
          marginTop: missionPanelVisible ? '-12px' : 0,
          pointerEvents: 'auto',
          background: missionPanelVisible || missions.length > 0 ? 'var(--bgSecondary)' : 'transparent',
          boxShadow: missionPanelVisible || missions.length > 0 ? '0 8px 32px rgba(0,0,0,0.45)' : 'none',
          minHeight: 0,
          WebkitOverflowScrolling: 'touch',
          display: !missionPanelVisible && missions.length === 0 ? 'none' : 'flex',
          flexDirection: 'column'
        }}>
          {missionPanelVisible && (
            <button
              type="button"
              onClick={() => setMissionListOpen(o => !o)}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', background: 'transparent', border: 'none', padding: 0, margin: '0 0 10px 0', cursor: 'pointer' }}
            >
              <span style={{ color: 'var(--textMuted)', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 600 }}>Drone Missions</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ color: 'var(--textMuted)', fontSize: '10px', fontWeight: 600 }}>{missions.length}</span>
                <span className="material-symbols-outlined" style={{ fontSize: '18px', color: 'var(--textMuted)', transition: 'transform 0.2s', transform: missionListOpen ? 'rotate(0deg)' : 'rotate(-90deg)' }}>expand_more</span>
              </span>
            </button>
          )}
          {(missionListOpen || !missionPanelVisible) && (
          <div className="custom-scrollbar" style={{ display: 'flex', flexDirection: 'column', gap: '8px', overflowY: 'auto', maxHeight: missionPanelVisible ? '240px' : 'none' }}>
          {missions.map(mission => (
            <div
              key={mission.id}
              className="mission-list-item"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                selectMission(mission.id);
              }}
              title={missionPanelVisible ? '' : `${mission.name} - ${mission.waypoints?.length || 0} waypoints`}
              style={{
                padding: missionPanelVisible ? '10px 12px' : '12px 0',
                marginBottom: '2px',
                background: selectedMission?.id === mission.id ? 'var(--bgPrimary)' : 'color-mix(in srgb, var(--textPrimary) 2.5%, transparent)',
                borderRadius: '8px',
                cursor: 'pointer',
                border: selectedMission?.id === mission.id ? '1px solid var(--accentSecondary)' : '1px solid color-mix(in srgb, var(--textPrimary) 6%, transparent)',
                borderLeft: selectedMission?.id === mission.id ? '3px solid var(--accent)' : '3px solid transparent',
                userSelect: 'none',
                display: 'flex',
                flexDirection: missionPanelVisible ? 'row' : 'column',
                alignItems: 'center',
                justifyContent: missionPanelVisible ? 'space-between' : 'center',
                transition: 'all 0.15s ease',
                height: missionPanelVisible ? 'auto' : '50px',
                gap: '8px'
              }}
            >
              {missionPanelVisible ? (
                <>
                  <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                    <div style={{
                      color: selectedMission?.id === mission.id ? 'var(--accent)' : 'var(--textPrimary)',
                      fontWeight: 600,
                      fontSize: '13px',
                      marginBottom: '2px',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {mission.name}
                    </div>
                    <div style={{ color: 'var(--textMuted)', fontSize: '11px' }}>
                      {mission.waypoints?.length || 0} waypoints
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      deleteMission(mission.id, mission.name);
                    }}
                    title={`Delete "${mission.name}"`}
                    style={{
                      width: '28px',
                      height: '28px',
                      padding: 0,
                      background: 'transparent',
                      color: 'var(--textMuted)',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: 0.55,
                      transition: 'all 0.15s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = 'var(--danger)';
                      e.currentTarget.style.background = 'color-mix(in srgb, var(--danger) 15%, transparent)';
                      e.currentTarget.style.opacity = '1';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = 'var(--textMuted)';
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.opacity = '0.55';
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path><path d="M10 11v6"></path><path d="M14 11v6"></path></svg>
                  </button>
                </>
              ) : (
                <div style={{ color: selectedMission?.id === mission.id ? 'var(--accent)' : 'var(--textSecondary)', display: 'flex', alignItems: 'center' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>
                </div>
              )}
            </div>
          ))}
          {missions.length === 0 && missionPanelVisible && (
            <div style={{ padding: '16px', textAlign: 'center', color: 'var(--textMuted)', fontSize: '11px' }}>No missions yet — create one above</div>
          )}
          </div>
          )}
        </div>

        {isMobileLayout && missionPanelVisible && missionControllerCard}
      </div>

      {!isMobileLayout && missionControllerCard}

      {/* Main Map Area */}
      <div style={{ flex: 1, position: 'relative', minHeight: 0, overflow: 'hidden' }}>
        {/* Temporarily disabled 3D preview button */}
        {false && MAPBOX_TOKEN && (
          <button
            type="button"
            onClick={() => setView3D((v) => !v)}
            title={view3D ? 'Switch to 2D editing' : 'Preview this route in 3D'}
            style={{
              position: 'absolute', top: 12, right: 12, zIndex: 1200,
              padding: '6px 12px', borderRadius: 8,
              border: '1px solid var(--accent)',
              background: view3D ? 'var(--accent)' : 'rgba(2, 6, 23, 0.82)',
              color: view3D ? '#021014' : 'var(--accent)',
              fontWeight: 700, fontSize: 12, cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(2, 6, 23, 0.35)',
            }}
          >
            {view3D ? '2D Edit' : '3D Preview'}
          </button>
        )}

        <div style={{ position: 'absolute', inset: 0, display: view3D ? 'none' : 'block' }}>
        <MapContainer
          center={mapCenter}
          zoom={13}
          style={{ height: '100%', width: '100%', position: 'absolute', top: 0, left: 0 }}
          zoomControl={!isMobileLayout}
          maxZoom={22}
          minZoom={3}
          zoomSnap={0.25}
          zoomDelta={0.5}
          wheelPxPerZoomLevel={100}
        >
          <TileLayer
            key={activeMapStyle.id}
            attribution={activeMapStyle.attribution}
            url={activeMapStyle.url}
            tileSize={activeMapStyle.tileSize}
            zoomOffset={activeMapStyle.zoomOffset}
            maxZoom={22}
            maxNativeZoom={activeMapStyle.maxNativeZoom}
          />
          <MapInitializer
            mapRefObj={mapRef}
            onSetMapRef={stableSetMapRef}
            centerTarget={
              telemetry && telemetry.lat && telemetry.lon && (telemetry.lat !== 0 || telemetry.lon !== 0)
                ? [telemetry.lat, telemetry.lon]
                : (Array.isArray(mapCenter) ? mapCenter : DEFAULT_MAP_CENTER)
            }
          />
          <MapClickHandler selectedMission={selectedMission} deliveryMode={showDeliveryDialog} surveyMode={surveyMode} onMapClick={(lat, lon, type) => {
            if (surveyMode) {
              setSurveyPolygon(prev => [...prev, { lat, lng: lon }]);
            } else if (showDeliveryDialog) {
              setDeliveryCoords({ lat, lng: lon });
            } else {
              addWaypoint(lat, lon, type);
            }
          }} waypointType={'WAYPOINT'} />

          {/* Survey area polygon + vertices + live grid preview (Tier 2.4) */}
          {surveyMode && surveyPolygon.length > 0 && (
            <>
              <Polygon
                positions={surveyPolygon.map(p => [p.lat, p.lng])}
                pathOptions={{ color: '#f59e0b', weight: 2, fillOpacity: 0.08, dashArray: '6 4' }}
              />
              {surveyPolygon.map((p, i) => (
                <Marker
                  key={`survey-vtx-${i}`}
                  position={[p.lat, p.lng]}
                  icon={L.divIcon({
                    className: 'survey-vertex',
                    html: `<div style="background:var(--warning);color:#1a1a1a;border-radius:50%;width:18px;height:18px;display:flex;align-items:center;justify-content:center;font-size:10px;font-weight:700;border:2px solid white;">${i + 1}</div>`,
                    iconSize: [18, 18],
                    iconAnchor: [9, 9],
                  })}
                />
              ))}
            </>
          )}
          {surveyMode && surveyPreview && (
            <Polyline
              positions={surveyPreview.waypoints.map(wp => [wp.latitude, wp.longitude])}
              color="#f59e0b"
              weight={2}
              opacity={0.9}
            />
          )}

          {/* Waypoint Markers (skip non-location types like RTL that have no map position) */}
          {waypoints.filter(wp => !NON_LOCATION_WP_TYPES.includes(wp.type)).map((wp, index) => (
            <DraggableMarker
              key={wp.id}
              position={[wp.latitude, wp.longitude]}
              waypoint={wp}
              index={waypoints.indexOf(wp)}
              onDragStart={handleWaypointDragStart}
              onDragEnd={handleWaypointDragEnd}
              onEdit={() => {
                // Legacy onEdit - editing is now done in bottom panel
                logger.log('Edit waypoint:', wp.id);
                setSelectedWaypointId(wp.id);
              }}
              onSelect={setSelectedWaypointId}
              onDelete={() => deleteWaypoint(wp.id)}
              type={wp.type}
              isSelected={selectedWaypointId === wp.id}
            />
          ))}

          {/* Home Location Marker */}
          {homeLocation && (
            <Marker 
              position={[homeLocation.lat, homeLocation.lon]} 
              draggable={true}
              eventHandlers={{
                dragend: (e) => {
                  const marker = e.target;
                  const position = marker.getLatLng();
                  const newHome = { lat: position.lat, lon: position.lng };
                  setHomeLocation(newHome);
                  homeLocationRef.current = newHome;
                  showToast('Home location updated manually', 'info');
                },
              }}
              icon={L.divIcon({
                className: 'home-marker',
                html: `<div style="background: var(--success); color: white; border-radius: 50%; width: 24px; height: 24px; display: flex; items-align: center; justify-content: center; font-weight: bold; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);">H</div>`,
                iconSize: [24, 24],
                iconAnchor: [12, 12]
              })}
            >
              <Popup>
                <div style={{ padding: '4px' }}>
                  <strong style={{ display: 'block', marginBottom: '4px' }}>Home Location</strong>
                  <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '8px' }}>
                    {homeLocation.lat.toFixed(6)}, {homeLocation.lon.toFixed(6)}
                  </div>
                  <div style={{ fontSize: '10px', color: '#94a3b8', fontStyle: 'italic', marginBottom: '8px' }}>
                    Drag this marker to manually set a new Home position.
                  </div>
                  <button 
                    onClick={() => {
                      setMapCenter([homeLocation.lat, homeLocation.lon]);
                      mapRef.current?.setView([homeLocation.lat, homeLocation.lon], 18);
                    }}
                    style={{ width: '100%', padding: '6px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}
                  >
                    Center Map
                  </button>
                  <button 
                    onClick={() => {
                      // Attempt to re-sync home from current drone telemetry
                      const currentTel = dronesTelemetry[selectedDrone];
                      if (currentTel && currentTel.lat && currentTel.lon) {
                        const syncedHome = { lat: currentTel.lat, lon: currentTel.lon };
                        setHomeLocation(syncedHome);
                        homeLocationRef.current = syncedHome;
                        showToast('Home re-synced to drone position', 'success');
                      } else {
                        showToast('Drone telemetry unavailable for sync', 'warning');
                      }
                    }}
                    style={{ marginTop: '4px', width: '100%', padding: '6px', background: 'transparent', color: 'var(--success)', border: '1px solid var(--success)', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}
                  >
                    Sync to Drone
                  </button>
                </div>
              </Popup>
            </Marker>
          )}

          {/* Waypoint Path */}
          {waypointPositions.length > 1 && (
            <Polyline
              positions={waypointPositions}
              color="var(--accent)"
              weight={3}
              opacity={0.7}
            />
          )}

          {/* Active Drone Markers */}
          {ActiveFleetMarkers}
        </MapContainer>
        </div>

        {view3D && MAPBOX_TOKEN && (
          <div style={{ position: 'absolute', inset: 0 }}>
            <Suspense fallback={<div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f172a', color: '#5eead4', fontWeight: 600 }}>Loading 3D preview…</div>}>
              <Map3DRenderer
                location={Array.isArray(mapCenter) ? { lat: mapCenter[0], lon: mapCenter[1] } : { lat: 21.1458, lon: 79.0882 }}
                dronesTelemetry={dronesTelemetry}
                drones={previewDrones}
                localRoutes={previewLocalRoutes}
                isActive={view3D}
                currentUserRole="operator"
              />
            </Suspense>
          </div>
        )}

        {/* Map style picker — desktop: left of Select Drones card; phone: right column under search */}
        <div style={{
          position: 'absolute',
          ...(isMobileLayout
            ? { right: '16px', top: 'calc(var(--m-top) + 128px)' }
            : { right: '328px', top: '16px' }),
          zIndex: 1001,
        }}>
          <button
            className={isMobileLayout ? `m-b m-glass m-round${showMapStyleMenu ? ' active' : ''}` : `glass-panel${showMapStyleMenu ? ' active-glow' : ''}`}
            title="Map style"
            onClick={() => setShowMapStyleMenu(v => !v)}
            style={{ width: '48px', height: '48px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: isMobileLayout ? undefined : 'var(--accent)' }}
          >
            <span className="material-symbols-outlined">layers</span>
          </button>

          {showMapStyleMenu && (
            <div className="glass-panel" style={{
              position: 'absolute',
              right: 0,
              top: '56px',
              width: '170px',
              padding: '6px',
              display: 'flex',
              flexDirection: 'column',
              gap: '2px'
            }}>
              {MAP_STYLES.map(s => (
                <button
                  key={s.id}
                  onClick={() => { setMapStyleId(s.id); setShowMapStyleMenu(false); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '10px', width: '100%',
                    padding: '8px 10px', borderRadius: '8px', border: 'none', cursor: 'pointer',
                    background: mapStyleId === s.id ? 'color-mix(in srgb, var(--accent) 12%, transparent)' : 'transparent',
                    color: mapStyleId === s.id ? 'var(--accent)' : 'var(--textSecondary)',
                    fontSize: '12px', fontWeight: 600, letterSpacing: '0.3px', textAlign: 'left',
                    fontFamily: "'JetBrains Mono', monospace"
                  }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>{s.icon}</span>
                  {s.label}
                  {mapStyleId === s.id && <span className="material-symbols-outlined" style={{ fontSize: '16px', marginLeft: 'auto' }}>check</span>}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Survey / Grid control overlay (Tier 2.4) */}
        {surveyMode && (
          <SurveyGridPanel
            params={surveyParams}
            onParamChange={handleSurveyParamChange}
            vertexCount={surveyPolygon.length}
            preview={surveyPreview}
            onGenerate={generateSurveyWaypoints}
            onUndo={undoSurveyPoint}
            onClear={clearSurveyPolygon}
            onCancel={exitSurveyMode}
          />
        )}

        {/* Bottom Waypoint Panel */}
        <BottomWaypointPanel
          waypoints={waypoints}
          onUpdateWaypoint={updateWaypoint}
          onDeleteWaypoint={deleteWaypoint}
          onReorderWaypoint={reorderWaypoint}
          isVisible={bottomPanelVisible}
          onToggle={() => setBottomPanelVisible(!bottomPanelVisible)}
          selectedWaypointId={selectedWaypointId}
          onSelectWaypoint={setSelectedWaypointId}
          isMobileLayout={isMobileLayout}
        />
      </div>

      {/* Input Modal for Mission Creation */}
      <InputModal
        isOpen={showInputModal}
        onClose={() => setShowInputModal(false)}
        onConfirm={handleMissionCreate}
        title="Create New Mission"
        placeholder="Enter mission name (e.g., Survey Area A)"
        maxLength={50}
      />

      {/* Delivery Dialog */}
      <DeliveryDialog
        isOpen={showDeliveryDialog}
        onClose={() => { setShowDeliveryDialog(false); setDeliveryCoords(null); }}
        selectedDrone={selectedDrone}
        droneConnected={droneConnected}
        deliveryCoords={deliveryCoords}
        dronePos={telemetry}
        onDeliveryCreated={async (mission) => {
          // Reload missions list and select the new delivery mission
          await loadMissions(selectedDrone);
          setSelectedMission(mission);
          setWaypoints(mission.waypoints || []);
          setMissionUploaded(true);
          showToast('Delivery mission created and uploaded! Click Start to begin.', 'success');
        }}
      />

      {/* Progress Indicator for Async Operations */}
      <ProgressIndicator
        isVisible={progressState.visible}
        message={progressState.message}
        type={progressState.type}
        onClose={() => setProgressState({ visible: false, message: '', type: 'default' })}
      />

      {/* Pre-flight Checklist Modal */}
      {showPreflight && (
        <PreflightChecklist
          droneId={selectedDrone}
          missionId={selectedMission?.id}
          onClose={() => setShowPreflight(false)}
          onProceed={() => { setShowPreflight(false); startMission(); }}
          onForceProceed={() => { setShowPreflight(false); startMission({ forceArm: true }); }}
        />
      )}

      {/* Altitude-layer picker */}
      {layerPicker && (
        <div
          onClick={() => setLayerPicker(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(5, 7, 10, 0.85)', backdropFilter: 'blur(8px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'linear-gradient(135deg, #181c25 0%, #0e1117 100%)', border: '1px solid color-mix(in srgb, var(--textPrimary) 12%, transparent)',
              width: '90%', maxWidth: '460px', maxHeight: '85vh', overflowY: 'auto', padding: '22px', color: '#fff', borderRadius: '4px',
            }}
          >
            <h3 style={{ margin: '0 0 4px', fontSize: '16px', fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
              Altitude Layers
            </h3>
            {layerPicker.loading ? (
              <p style={{ fontSize: '12px', color: 'color-mix(in srgb, var(--textPrimary) 60%, transparent)' }}>Checking every layer against claimed airspace…</p>
            ) : (
              <>
                <p style={{ margin: '0 0 14px', fontSize: '12px', color: 'color-mix(in srgb, var(--textPrimary) 55%, transparent)', lineHeight: 1.5 }}>
                  {layerPicker.currentLayer
                    ? `Cruise is at ${layerPicker.cruiseAltitude} m, in layer ${layerPicker.currentLayer}. `
                    : `Cruise is at ${layerPicker.cruiseAltitude ?? '—'} m, outside the layers. `}
                  Pick a free layer to move the cruise there. Takeoff, landing and drop heights stay as they are.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {[...layerPicker.layers].reverse().map((l) => {
                    const free = l.status === 'free';
                    const color = free ? (l.belowPlanned ? 'var(--warning)' : 'var(--success)') : l.status === 'occupied' ? 'var(--danger)' : 'color-mix(in srgb, var(--textPrimary) 35%, transparent)';
                    return (
                      <button
                        key={l.layer}
                        disabled={!free}
                        onClick={() => pickLayer(l)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '12px', padding: '9px 12px', textAlign: 'left',
                          background: l.layer === layerPicker.currentLayer ? 'rgba(59, 130, 246, 0.12)' : 'rgba(0,0,0,0.25)',
                          border: `1px solid ${free ? 'color-mix(in srgb, var(--success) 40%, transparent)' : 'color-mix(in srgb, var(--textPrimary) 6%, transparent)'}`,
                          color: '#fff', cursor: free ? 'pointer' : 'not-allowed', opacity: free ? 1 : 0.75,
                        }}
                      >
                        <span style={{ width: '10px', height: '10px', borderRadius: '50%', flexShrink: 0, background: layerColor(l.layer, layerPicker.layers.length) }} title="Corridor colour of this layer on the 3D map" />
                        <span style={{ width: '34px', fontWeight: 800, fontSize: '13px' }}>L{l.layer}</span>
                        <span style={{ width: '120px', fontFamily: 'monospace', fontSize: '12px' }}>
                          {l.altitude} m <span style={{ color: 'color-mix(in srgb, var(--textPrimary) 40%, transparent)' }}>({l.from}–{l.to})</span>
                        </span>
                        <span style={{ flex: 1, fontSize: '11px', color }}>
                          {free ? (l.belowPlanned ? 'Free · below your planned height — check obstacles' : 'Free') : l.status === 'occupied' ? `Occupied by ${l.conflictWith}` : l.reason}
                          {l.layer === layerPicker.currentLayer ? ' · current' : ''}
                          {l.conflictWhere && (
                            <span style={{ display: 'block', marginTop: '2px', color: 'color-mix(in srgb, var(--textPrimary) 45%, transparent)', fontWeight: 400, textTransform: 'none' }}>
                              {l.conflictWhere}
                            </span>
                          )}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
            <button
              onClick={() => setLayerPicker(null)}
              style={{
                width: '100%', marginTop: '14px', padding: '10px', background: 'transparent', border: '1px solid color-mix(in srgb, var(--textPrimary) 15%, transparent)',
                color: 'color-mix(in srgb, var(--textPrimary) 70%, transparent)', cursor: 'pointer', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase',
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Route Collision / Deconfliction Modal */}
      {showConflictModal && conflictDetails && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(5, 7, 10, 0.85)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #181c25 0%, #0e1117 100%)',
            border: '1px solid color-mix(in srgb, var(--danger) 35%, transparent)',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.7), 0 0 30px color-mix(in srgb, var(--danger) 10%, transparent)',
            width: '90%',
            maxWidth: '520px',
            padding: '28px',
            color: '#fff',
            borderRadius: '4px',
            position: 'relative',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '20px' }}>
              <div style={{
                background: 'color-mix(in srgb, var(--danger) 15%, transparent)',
                border: '1px solid var(--danger)',
                borderRadius: '50%',
                width: '42px',
                height: '42px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 0 15px color-mix(in srgb, var(--danger) 20%, transparent)',
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
              </div>
              <div>
                <h3 style={{
                  margin: 0,
                  fontSize: '18px',
                  fontWeight: 800,
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  color: 'var(--danger)',
                }}>
                  Collision Detected
                </h3>
                <span style={{ fontSize: '11px', color: 'color-mix(in srgb, var(--textPrimary) 45%, transparent)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  UTM Strategic Deconfliction Engine
                </span>
              </div>
            </div>

            <p style={{
              margin: '0 0 18px 0',
              fontSize: '13px',
              lineHeight: '1.6',
              color: 'color-mix(in srgb, var(--textPrimary) 85%, transparent)',
            }}>
              The proposed flight corridor overlaps with the active flight path of{' '}
              <strong style={{ color: '#3b82f6' }}>
                {conflictDetails.details.droneName}
              </strong>{' '}
              ({conflictDetails.details.missionName}).
            </p>

            <div style={{
              background: 'rgba(0, 0, 0, 0.35)',
              border: '1px solid color-mix(in srgb, var(--textPrimary) 5%, transparent)',
              borderRadius: '4px',
              padding: '14px',
              marginBottom: '24px',
              fontSize: '12px',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: 'color-mix(in srgb, var(--textPrimary) 40%, transparent)' }}>Conflict Segment Index:</span>
                <span style={{ fontWeight: 600, color: '#fff' }}>Waypoint #{conflictDetails.details.wpIndexA_Start}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: 'color-mix(in srgb, var(--textPrimary) 40%, transparent)' }}>Estimated Proximity:</span>
                <span style={{ fontWeight: 600, color: 'var(--danger)' }}>
                  {conflictDetails.details.distance.toFixed(2)} m (Safety Buffer: {conflictDetails.details.safetyDistance ?? 16}m)
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'color-mix(in srgb, var(--textPrimary) 40%, transparent)' }}>Conflict Location:</span>
                <span style={{ fontWeight: 600, fontFamily: 'monospace', color: '#fff' }}>
                  {conflictDetails.details.point1.lat.toFixed(5)}, {conflictDetails.details.point1.lon.toFixed(5)}
                </span>
              </div>
            </div>

            <div style={{
              fontSize: '11px',
              color: 'color-mix(in srgb, var(--textPrimary) 50%, transparent)',
              marginBottom: '20px',
              textTransform: 'uppercase',
              letterSpacing: '0.02em',
            }}>
              Select a deconfliction resolution option to auto-adjust route:
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button
                onClick={() => { setShowConflictModal(false); openLayerPicker(); }}
                style={{
                  width: '100%', padding: '12px 16px', background: 'color-mix(in srgb, var(--success) 10%, transparent)',
                  border: '1px solid color-mix(in srgb, var(--success) 40%, transparent)', color: 'var(--success)', textAlign: 'left', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}
              >
                <div>
                  <div style={{ fontWeight: 700, fontSize: '13px' }}>Choose Altitude Layer</div>
                  <div style={{ fontSize: '11px', color: 'color-mix(in srgb, var(--textPrimary) 40%, transparent)', marginTop: '2px' }}>
                    See which of the 10 layers are free and move the cruise to one
                  </div>
                </div>
                <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>layers</span>
              </button>

              <button
                onClick={() => saveMission(conflictDetails.resolutions.vertical)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  background: 'rgba(59, 130, 246, 0.1)',
                  border: '1px solid rgba(59, 130, 246, 0.4)',
                  color: '#3b82f6',
                  textAlign: 'left',
                  cursor: 'pointer',
                  borderRadius: '0px',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <div>
                  <div style={{ fontWeight: 700, fontSize: '13px' }}>Auto-Avoid (Climb Over)</div>
                  <div style={{ fontSize: '11px', color: 'color-mix(in srgb, var(--textPrimary) 40%, transparent)', marginTop: '2px' }}>
                    Climbs the conflicting segment clear of the buffer, then descends
                  </div>
                </div>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="m18 15-6-6-6 6"/>
                </svg>
              </button>

              <button
                onClick={() => saveMission(conflictDetails.resolutions.horizontal)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  background: 'rgba(139, 92, 246, 0.1)',
                  border: '1px solid rgba(139, 92, 246, 0.4)',
                  color: '#8b5cf6',
                  textAlign: 'left',
                  cursor: 'pointer',
                  borderRadius: '0px',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <div>
                  <div style={{ fontWeight: 700, fontSize: '13px' }}>Auto-Avoid (Side-step)</div>
                  <div style={{ fontSize: '11px', color: 'color-mix(in srgb, var(--textPrimary) 40%, transparent)', marginTop: '2px' }}>
                    3D bypass: offsets the segment sideways and climbs clear
                  </div>
                </div>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="m9 18 6-6-6-6"/>
                </svg>
              </button>

              <button
                onClick={() => {
                  setShowConflictModal(false);
                  setConflictDetails(null);
                }}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  background: 'transparent',
                  border: '1px solid color-mix(in srgb, var(--textPrimary) 15%, transparent)',
                  color: 'color-mix(in srgb, var(--textPrimary) 70%, transparent)',
                  textAlign: 'center',
                  cursor: 'pointer',
                  borderRadius: '0px',
                  transition: 'all 0.2s',
                  fontSize: '12px',
                  fontWeight: 700,
                  marginTop: '10px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                Manual Resolve / Adjust Route
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}



export default MissionPlanner;



